import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { 
  ShoppingBag, Check, Truck, Phone, MapPin, AlertCircle, 
  Trash2, Plus, Minus, Heart, ShieldCheck, Star, 
  ArrowRight, X, Sparkles, MessageCircle, User, LogOut, Lock, Mail, ClipboardList, TrendingUp, Store, Award
} from "lucide-react";
import axios from "axios";
import { Toaster, toast } from "sonner";
import "@/App.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

const LOGO_IMAGE_URL = "/logo.jpeg";

const CopyableText = ({ text, label }) => {
  const [copied, setCopied] = useState(false);
  
  const handleCopy = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success(`${label || "Texto"} copiado`);
    setTimeout(() => setCopied(false), 2000);
  };
  
  return (
    <div 
      className="inline-flex items-center justify-between gap-3 bg-white hover:bg-slate-50 border border-slate-200/60 px-3.5 py-2.5 rounded-xl transition-all cursor-pointer select-all font-mono text-xs font-black text-slate-800 shadow-sm w-full" 
      onClick={handleCopy} 
      title="Haz clic para copiar"
    >
      <span className="truncate">{text}</span>
      {copied ? (
        <Check className="h-3.5 w-3.5 text-green-600 stroke-[3] flex-shrink-0" />
      ) : (
        <svg className="h-3.5 w-3.5 text-slate-400 hover:text-[#00BACF] flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M8 7v8a2 2 0 002 2h6M8 7V5a2 2 0 012-2h4.586a1 1 0 01.707.293l4.414 4.414a1 1 0 01.293.707V15a2 2 0 01-2 2h-2M8 7H6a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2v-2" />
        </svg>
      )}
    </div>
  );
};

export default function App() {
  const [products, setProducts] = useState([]);
  const [cart, setCart] = useState([]);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  
  // Auth states
  const [user, setUser] = useState(null);
  const [authModal, setAuthModal] = useState(null); // 'login' | 'register' | null
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [userOrders, setUserOrders] = useState([]);
  const [allStoreOrders, setAllStoreOrders] = useState([]);
  const [adminTab, setAdminTab] = useState("orders");
  const [orderFilter, setOrderFilter] = useState("all"); // 'all' | 'processing' | 'completed' | 'failed'
  const [selectedOrders, setSelectedOrders] = useState([]);

  // Auth Inputs
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [registerForm, setRegisterForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    password: ""
  });

  // Success states
  const [successOrder, setSuccessOrder] = useState(null);
  const [isSuccessOpen, setIsSuccessOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Card quantities
  const [selectedQuantities, setSelectedQuantities] = useState({});

  // Checkout inputs
  const [checkoutForm, setCheckoutForm] = useState({
    name: "",
    email: "",
    phone: "",
    deliveryType: "pickup",
    deliveryAddress: ""
  });

  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState("stripe");
  const [pagoMovilDetails, setPagoMovilDetails] = useState({
    bank_phone: "",
    reference: "",
    amount_paid: ""
  });

  // Interactive Calculator State for Wholesalers
  const [calcBudget, setCalcBudget] = useState(150);

  // Currency Selector & Exchange Rate (VES) States
  const [currency, setCurrency] = useState("USD");
  const [exchangeRate, setExchangeRate] = useState(742.23);

  // Admin Path Gating & Homepage Settings States
  const [isAdminPath, setIsAdminPath] = useState(false);
  const [homepageSettings, setHomepageSettings] = useState({
    featured_image: "https://customer-assets-7cd3h4nn.emergentagent.net/job_b59888f0-73db-4daf-a5c7-95d1e906064f/artifacts/qqehzx5z_Diapositiva1.PNG"
  });

  // Check on mount if URL path is an administrative URL
  useEffect(() => {
    const checkAdminRoute = () => {
      const path = window.location.pathname;
      const hash = window.location.hash;
      const search = window.location.search;
      if (
        path === "/admin" || 
        path === "/secreto-admin" || 
        hash === "#admin" || 
        hash === "#secreto-admin" ||
        search.includes("admin-access=true")
      ) {
        setIsAdminPath(true);
      }
    };
    checkAdminRoute();
  }, []);

  // Fetch Homepage settings (Featured Image banner) from Firestore CMS
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await axios.get(`${API}/settings/homepage`);
        if (res.data && res.data.featured_image) {
          setHomepageSettings(res.data);
        }
      } catch (e) {
        console.error("Error fetching homepage settings:", e);
      }
    };
    fetchSettings();
  }, []);

  const [paymentMethods, setPaymentMethods] = useState({
    stripe: { enabled: true, name: "Tarjeta de Crédito / Stripe" },
    pago_movil: {
      enabled: true,
      name: "Pago Móvil",
      details: "Banco Banesco (0134) • Teléfono: 0422-8098318 • C.I.: V-12345678"
    }
  });

  useEffect(() => {
    const fetchPaymentMethods = async () => {
      try {
        const res = await axios.get(`${API}/settings/payment-methods`);
        if (res.data) {
          setPaymentMethods(res.data);
        }
      } catch (e) {
        console.error("Error fetching payment methods:", e);
      }
    };
    fetchPaymentMethods();
  }, []);

  const [inputExchangeRate, setInputExchangeRate] = useState(742.23);

  useEffect(() => {
    setInputExchangeRate(exchangeRate);
  }, [exchangeRate]);

  const handleSaveExchangeRateCMS = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/settings/exchange-rate`, { rate: parseFloat(inputExchangeRate) });
      setExchangeRate(parseFloat(inputExchangeRate));
      toast.success("¡Tasa de cambio manual guardada exitosamente!");
    } catch (e) {
      console.error("Error saving rate:", e);
      toast.error("Ocurrió un error al guardar la tasa.");
    } finally {
      setLoading(false);
    }
  };

  const handleSavePaymentMethodsCMS = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/settings/payment-methods`, paymentMethods);
      toast.success("¡Métodos de pago actualizados exitosamente!");
    } catch (e) {
      console.error("Error saving payment methods:", e);
      toast.error("Error al actualizar los métodos de pago.");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateOrderStatus = async (orderId, newStatus, newPaymentStatus) => {
    setLoading(true);
    try {
      await axios.post(`${API}/orders/${orderId}/status`, {
        status: newStatus,
        payment_status: newPaymentStatus
      });
      toast.success(`Pedido actualizado a: ${newStatus === "completed" ? "Aprobado" : newStatus === "failed" ? "Rechazado" : "En Proceso"}`);
      // Refresh orders
      fetchAllStoreOrders();
    } catch (e) {
      console.error("Error updating order:", e);
      toast.error("Error al actualizar el estado del pedido.");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteOrder = async (orderId) => {
    if (!window.confirm("¿Estás segura de que deseas eliminar permanentemente este pedido de la base de datos?")) {
      return;
    }
    
    // Optimistic local state update to instantly clear the order from screen
    setAllStoreOrders(prev => prev.filter(o => o.stripe_session_id !== orderId));
    setSelectedOrders(prev => prev.filter(id => id !== orderId));
    
    setLoading(true);
    try {
      await axios.delete(`${API}/orders/${orderId}`);
      toast.success("¡Pedido eliminado permanentemente con éxito!");
      // Re-fetch to ensure sync
      fetchAllStoreOrders();
    } catch (e) {
      console.error("Error deleting order:", e);
      toast.error("Error al eliminar el pedido.");
      // Rollback on error
      fetchAllStoreOrders();
    } finally {
      setLoading(false);
    }
  };

  const handleBulkUpdateStatus = async (newStatus, newPaymentStatus) => {
    if (selectedOrders.length === 0) return;
    setLoading(true);
    try {
      await axios.post(`${API}/orders/bulk-status`, {
        order_ids: selectedOrders,
        status: newStatus,
        payment_status: newPaymentStatus
      });
      toast.success(`¡${selectedOrders.length} pedidos actualizados con éxito!`);
      setSelectedOrders([]);
      fetchAllStoreOrders();
    } catch (err) {
      console.error("Bulk status error:", err);
      toast.error("Error al actualizar los pedidos en bloque.");
    } finally {
      setLoading(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedOrders.length === 0) return;
    if (!window.confirm(`¿Estás segura de que deseas eliminar permanentemente estos ${selectedOrders.length} pedidos?`)) {
      return;
    }
    setLoading(true);
    
    // Optimistic local state update to instantly clear deleted items from screen
    const remaining = allStoreOrders.filter(o => !selectedOrders.includes(o.stripe_session_id));
    setAllStoreOrders(remaining);
    
    try {
      await axios.post(`${API}/orders/bulk-delete`, {
        order_ids: selectedOrders
      });
      toast.success("¡Pedidos eliminados permanentemente exitosamente!");
      setSelectedOrders([]);
      fetchAllStoreOrders();
    } catch (err) {
      console.error("Bulk delete error:", err);
      toast.error("Error al eliminar los pedidos en bloque.");
      fetchAllStoreOrders();
    } finally {
      setLoading(false);
    }
  };

  const handleImageUploadCMS = async (productId, file) => {
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    setLoading(true);
    try {
      const res = await axios.post(`${API}/upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      const uploadedUrl = `${BACKEND_URL}${res.data.url}`;
      handleUpdateProductField(productId, "image", uploadedUrl);
      toast.success("¡Imagen de producto subida con éxito! Presiona 'Guardar Cambios' para aplicar.");
    } catch (e) {
      console.error("Error uploading image:", e);
      toast.error("Ocurrió un error al subir el archivo de imagen.");
    } finally {
      setLoading(false);
    }
  };

  const handleFeaturedImageUploadCMS = async (file) => {
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    setLoading(true);
    try {
      const res = await axios.post(`${API}/upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      const uploadedUrl = `${BACKEND_URL}${res.data.url}`;
      
      // Save directly to settings endpoint
      await axios.post(`${API}/settings/homepage`, { featured_image: uploadedUrl });
      setHomepageSettings({ featured_image: uploadedUrl });
      toast.success("¡Imagen destacada (Banner) guardada exitosamente!");
    } catch (e) {
      console.error("Error uploading featured image:", e);
      toast.error("Ocurrió un error al subir el archivo de imagen destacada.");
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (usdAmount) => {
    if (currency === "VES") {
      const vesAmount = usdAmount * exchangeRate;
      return `Bs. ${vesAmount.toLocaleString("es-VE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }
    return `$${usdAmount.toFixed(2)}`;
  };

  useEffect(() => {
    const fetchRate = async () => {
      try {
        const res = await axios.get(`${API}/bcv-rate`);
        if (res.data && res.data.rate) {
          setExchangeRate(res.data.rate);
        }
      } catch (err) {
        console.error("Error fetching BCV rate:", err);
      }
    };
    fetchRate();
  }, []);

  // Fetch products
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const res = await axios.get(`${API}/products`, { timeout: 3000 });
        setProducts(res.data);
        const initialQtys = {};
        res.data.forEach(p => {
          initialQtys[p.id] = p.min_qty;
        });
        setSelectedQuantities(initialQtys);
      } catch (err) {
        console.error("Error fetching products:", err);
        const fallback = [
          {
            "id": "yogurt_liquido_200ml",
            "name": "Yogurt Líquido Fresa (200 ml)",
            "price": 1.4,
            "min_qty": 12,
            "image": "https://images.pexels.com/photos/2064359/pexels-photo-2064359.jpeg",
            "description": "Yogurt líquido de fresa, cremoso y saludable. Envase de 200 ml. Pedido mínimo de 12 unidades.",
            "badge": "Mayorista",
            "size": "200 ml"
          },
          {
            "id": "yogurt_liquido_400ml",
            "name": "Yogurt Líquido Fresa (400 ml)",
            "price": 2.4,
            "min_qty": 6,
            "image": "https://images.pexels.com/photos/2064366/pexels-photo-2064366.jpeg",
            "description": "Yogurt líquido de fresa, cremoso y refrescante. Envase de 400 ml. Pedido mínimo de 6 unidades.",
            "badge": "Mayorista",
            "size": "400 ml"
          },
          {
            "id": "yogurt_liquido_900ml",
            "name": "Yogurt Líquido Fresa (900 ml)",
            "price": 4.75,
            "min_qty": 6,
            "image": "https://images.pexels.com/photos/27690925/pexels-photo-27690925.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
            "description": "Yogurt líquido de fresa, ideal para compartir. Envase de 900 ml. Pedido mínimo de 6 unidades.",
            "badge": "Familiar",
            "size": "900 ml"
          },
          {
            "id": "yogurt_liquido_1l",
            "name": "Yogurt Líquido Fresa (1 Litro)",
            "price": 4.99,
            "min_qty": 1,
            "image": homepageSettings.featured_image,
            "description": "La clásica presentación de 1 Litro para disfrutar cuando quieras.",
            "badge": "Clásico",
            "size": "1 Litro"
          },
          {
            "id": "yogurt_firme_500g",
            "name": "Yogurt Firme (500 gr)",
            "price": 4.3,
            "min_qty": 6,
            "image": "https://images.pexels.com/photos/1998893/pexels-photo-1998893.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
            "description": "Yogurt firme, espeso y delicioso de 500 gramos. Pedido mínimo de 6 unidades.",
            "badge": "Premium",
            "size": "500 gr"
          },
          {
            "id": "combo_emprendedor",
            "name": "Combo Emprendedor Nutriyog",
            "price": 50.0,
            "min_qty": 1,
            "image": "https://images.pexels.com/photos/4736077/pexels-photo-4736077.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
            "description": "Combo ideal para emprender: 6 Yogures Firmes (500g), 6 Líquidos (400ml) y 3 Líquidos (900ml).",
            "badge": "Recomendado",
            "size": "Combo Ahorro"
          }
        ];
        setProducts(fallback);
        const initialQtys = {};
        fallback.forEach(p => {
          initialQtys[p.id] = p.min_qty;
        });
        setSelectedQuantities(initialQtys);
      }
    };
    fetchProducts();
  }, []);

  // Restore session
  useEffect(() => {
    const savedUser = localStorage.getItem("nutriyog_user");
    if (savedUser) {
      try {
        const u = JSON.parse(savedUser);
        setUser(u);
        setCheckoutForm(prev => ({
          ...prev,
          name: `${u.first_name} ${u.last_name}`,
          email: u.email,
          phone: u.phone
        }));
      } catch (e) {
        localStorage.removeItem("nutriyog_user");
      }
    }
  }, []);

  // Fetch orders from Firestore
  const fetchUserOrders = async (userId) => {
    try {
      const res = await axios.get(`${API}/orders/user/${userId}`);
      setUserOrders(res.data);
    } catch (e) {
      console.error("Error fetching user orders:", e);
    }
  };

  const fetchAllStoreOrders = async () => {
    try {
      const res = await axios.get(`${API}/orders`);
      setAllStoreOrders(res.data);
    } catch (e) {
      console.error("Error fetching all store orders:", e);
    }
  };

  useEffect(() => {
    if (user) {
      fetchUserOrders(user.email);
      if (user.email === "admin@nutriyog.com.ve" || user.email === "DONATEX") {
        fetchAllStoreOrders();
      }
    } else {
      setUserOrders([]);
      setAllStoreOrders([]);
    }
  }, [user, isProfileOpen]);

  const [editingProductsList, setEditingProductsList] = useState({});
  const [showAddProductForm, setShowAddProductForm] = useState(false);
  const [newProductForm, setNewProductForm] = useState({
    id: "",
    name: "",
    price: "",
    size: "",
    badge: "Campaña",
    min_qty: 12,
    image: "/logo.jpeg",
    description: ""
  });

  const handleCreateProductCMS = async (e) => {
    e.preventDefault();
    if (!newProductForm.id || !newProductForm.name || !newProductForm.price) {
      toast.error("Por favor completa los campos obligatorios (ID, Nombre, Precio).");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        ...newProductForm,
        id: newProductForm.id.trim().toLowerCase().replace(/\s+/g, "_"),
        price: parseFloat(newProductForm.price),
        min_qty: parseInt(newProductForm.min_qty)
      };
      
      await axios.post(`${API}/products/${payload.id}`, payload);
      toast.success(`¡Campaña/Producto "${payload.name}" creado con éxito!`);
      
      // Reset form
      setNewProductForm({
        id: "",
        name: "",
        price: "",
        size: "",
        badge: "Campaña",
        min_qty: 12,
        image: "/logo.jpeg",
        description: ""
      });
      setShowAddProductForm(false);
      
      // Refresh list
      const refreshRes = await axios.get(`${API}/products`);
      setProducts(refreshRes.data);
    } catch (err) {
      console.error("Error creating product:", err);
      toast.error("Error al guardar la campaña en Firebase.");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteProductCMS = async (productId, productName) => {
    if (!window.confirm(`¿Estás segura de que deseas eliminar "${productName}" del catálogo?`)) {
      return;
    }
    setLoading(true);
    try {
      await axios.delete(`${API}/products/${productId}`);
      toast.success(`¡Campaña/Producto "${productName}" eliminado exitosamente!`);
      
      // Refresh list
      const refreshRes = await axios.get(`${API}/products`);
      setProducts(refreshRes.data);
    } catch (err) {
      console.error("Error deleting product:", err);
      toast.error("Error al eliminar de Firebase.");
    } finally {
      setLoading(false);
    }
  };

  // Initialize CMS editing forms when products load
  useEffect(() => {
    if (products.length > 0) {
      const initialForm = {};
      products.forEach(p => {
        initialForm[p.id] = { ...p };
      });
      setEditingProductsList(initialForm);
    }
  }, [products]);

  const handleUpdateProductField = (productId, field, value) => {
    setEditingProductsList(prev => ({
      ...prev,
      [productId]: {
        ...prev[productId],
        [field]: value
      }
    }));
  };

  const handleSaveProductCMS = async (productId) => {
    setLoading(true);
    try {
      const updatedProduct = editingProductsList[productId];
      updatedProduct.price = parseFloat(updatedProduct.price);
      updatedProduct.min_qty = parseInt(updatedProduct.min_qty);
      
      await axios.post(`${API}/products/${productId}`, updatedProduct);
      toast.success(`¡Cambios guardados para ${updatedProduct.name}!`);
      
      // Refresh catalog on frontend
      const refreshRes = await axios.get(`${API}/products`);
      setProducts(refreshRes.data);
    } catch (e) {
      console.error("Error saving product:", e);
      toast.error("Error al guardar los cambios del producto en Firebase.");
    } finally {
      setLoading(false);
    }
  };

  // Check URL parameters
  useEffect(() => {
    const checkStripeSuccess = async () => {
      const params = new URLSearchParams(window.location.search);
      const sessionId = params.get("session_id");
      if (sessionId) {
        setLoading(true);
        try {
          const res = await axios.get(`${API}/payments/status/${sessionId}`);
          if (res.data) {
            setSuccessOrder(res.data);
            setIsSuccessOpen(true);
            toast.success("¡Pago procesado con éxito!");
            localStorage.removeItem("nutriyog_cart");
            setCart([]);
          }
        } catch (err) {
          console.error("Error fetching status:", err);
          toast.error("No pudimos validar tu pago automáticamente.");
        } finally {
          setLoading(false);
        }
      }

      // Secret Admin Access via URL param, hash or pathname
      const adminAccess = params.get("admin-access");
      if (adminAccess === "true" || window.location.pathname === "/secreto-admin" || window.location.hash === "#secreto-admin") {
        setAuthModal("login");
        setLoginForm({ email: "admin@nutriyog.com.ve", password: "" });
        toast.info("Acceso de Administrador Detectado. Ingrese su contraseña principal.");
      }
    };
    checkStripeSuccess();
  }, []);

  // Load cart
  useEffect(() => {
    const savedCart = localStorage.getItem("nutriyog_cart");
    if (savedCart) {
      try {
        setCart(JSON.parse(savedCart));
      } catch (e) {
        localStorage.removeItem("nutriyog_cart");
      }
    }
  }, []);

  const saveCart = (newCart) => {
    setCart(newCart);
    localStorage.setItem("nutriyog_cart", JSON.stringify(newCart));
  };

  const handleAddToCart = (product) => {
    const qtyToAdd = selectedQuantities[product.id] || product.min_qty;

    if (qtyToAdd < product.min_qty) {
      toast.warning(`La cantidad mínima para este producto es de ${product.min_qty} unidades.`);
      return;
    }

    const existing = cart.find(i => i.id === product.id);

    if (existing) {
      const updatedCart = cart.map(item => {
        if (item.id === product.id) {
          return { ...item, quantity: item.quantity + qtyToAdd };
        }
        return item;
      });
      saveCart(updatedCart);
    } else {
      saveCart([...cart, { ...product, quantity: qtyToAdd }]);
    }
    
    toast.success(`Añadido al carrito: ${product.name}`, {
      description: `${qtyToAdd} unidades agregadas.`,
    });
  };

  const handleCardQtyChange = (productId, amount, minQty) => {
    const current = selectedQuantities[productId] || minQty;
    const newVal = current + amount;
    if (newVal < minQty) {
      toast.warning(`No puedes seleccionar menos del mínimo de ${minQty} unidades.`);
      return;
    }
    setSelectedQuantities(prev => ({
      ...prev,
      [productId]: newVal
    }));
  };

  const handleUpdateQuantityInCart = (productId, newQty, minQty) => {
    if (newQty < minQty) {
      toast.error(`No puedes bajar de la cantidad mínima de ${minQty} para este producto.`);
      return;
    }
    const updated = cart.map(item => {
      if (item.id === productId) {
        return { ...item, quantity: newQty };
      }
      return item;
    });
    saveCart(updated);
  };

  const handleRemoveFromCart = (productId) => {
    const updated = cart.filter(item => item.id !== productId);
    saveCart(updated);
    toast.info("Producto eliminado del carrito.");
  };

  const getCartSubtotal = () => {
    return cart.reduce((total, item) => total + (item.price * item.quantity), 0);
  };

  const getCartTotalItems = () => {
    return cart.reduce((total, item) => total + item.quantity, 0);
  };

  const getShippingFee = (subtotal) => {
    if (checkoutForm.deliveryType === "pickup") return 0.0;
    return subtotal >= 30.0 ? 0.0 : 2.00;
  };

  // Auth operations
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/login`, loginForm);
      setUser(res.data.user);
      localStorage.setItem("nutriyog_user", JSON.stringify(res.data.user));
      setCheckoutForm(prev => ({
        ...prev,
        name: `${res.data.user.first_name} ${res.data.user.last_name}`,
        email: res.data.user.email,
        phone: res.data.user.phone
      }));
      setAuthModal(null);
      setLoginForm({ email: "", password: "" });
      toast.success(`¡Bienvenido de vuelta, ${res.data.user.first_name}!`);
      
      // Auto-redirect to Full Page Admin view if logged in user is admin
      if (res.data.user.email === "admin@nutriyog.com.ve" || res.data.user.email === "DONATEX") {
        setIsAdminPath(true);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al iniciar sesión.");
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/auth/register`, {
        first_name: registerForm.firstName,
        last_name: registerForm.lastName,
        email: registerForm.email,
        phone: registerForm.phone,
        password: registerForm.password
      });
      toast.success("¡Registro exitoso! Ya puedes iniciar sesión.");
      setAuthModal("login");
      setRegisterForm({ firstName: "", lastName: "", email: "", phone: "", password: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al registrarse.");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    setUser(null);
    localStorage.removeItem("nutriyog_user");
    setCheckoutForm({
      name: "",
      email: "",
      phone: "",
      deliveryType: "pickup",
      deliveryAddress: ""
    });
    setIsProfileOpen(false);
    toast.info("Sesión cerrada correctamente.");
  };

  // Stripe Checkout
  const handleCheckout = async (e) => {
    e.preventDefault();
    if (cart.length === 0) {
      toast.error("El carrito está vacío");
      return;
    }

    if (!checkoutForm.name || !checkoutForm.email || !checkoutForm.phone) {
      toast.error("Por favor completa los campos de contacto.");
      return;
    }

    if (checkoutForm.deliveryType === "delivery" && !checkoutForm.deliveryAddress) {
      toast.error("Por favor ingresa tu dirección de despacho.");
      return;
    }

    if (selectedPaymentMethod === "pago_movil") {
      if (!pagoMovilDetails.bank_phone || !pagoMovilDetails.reference || !pagoMovilDetails.amount_paid) {
        toast.error("Por favor completa los detalles de tu Pago Móvil.");
        return;
      }
    }

    setLoading(true);
    try {
      const res = await axios.post(`${API}/payments/checkout`, {
        items: cart.map(i => ({ lookup_key: i.id, quantity: i.quantity })),
        origin_url: window.location.origin,
        client_name: checkoutForm.name,
        client_email: checkoutForm.email,
        client_phone: checkoutForm.phone,
        delivery_type: checkoutForm.deliveryType,
        delivery_address: checkoutForm.deliveryAddress,
        user_id: user ? user.email : null,
        payment_method: selectedPaymentMethod,
        payment_details: selectedPaymentMethod === "pago_movil" ? pagoMovilDetails : null
      });

      if (res.data && res.data.payment_method === "pago_movil") {
        const statusRes = await axios.get(`${API}/payments/status/${res.data.session_id}`);
        setSuccessOrder(statusRes.data);
        setIsSuccessOpen(true);
        toast.success("¡Tu pedido Pago Móvil ha sido registrado exitosamente para revisión!");
        localStorage.removeItem("nutriyog_cart");
        setCart([]);
        setIsCartOpen(false);
      } else if (res.data && res.data.checkout_url) {
        toast.info("Redireccionando a la pasarela de pago Stripe...");
        window.location.href = res.data.checkout_url;
      }
    } catch (err) {
      console.error("Checkout error:", err);
      toast.error(err.response?.data?.detail || "Error al registrar el pedido.");
    } finally {
      setLoading(false);
    }
  };

  const sendWhatsAppNotification = (order) => {
    if (!order) return;
    const itemsText = order.items.map(i => `• ${i.quantity}x ${i.name} ($${(i.price * i.quantity).toFixed(2)})`).join("\n");
    const deliveryText = order.delivery_type === "delivery" 
      ? `Delivery a dirección: ${order.delivery_address}` 
      : "Retiro acordado en tienda";
      
    const message = `¡Hola Nutriyog! 🍓🥛 Acabo de realizar mi compra de distribuidor en línea.\n\n` +
      `*ID del Pedido:* ${order.stripe_session_id.substring(0, 12)}...\n` +
      `*Cliente:* ${order.client_name}\n` +
      `*Teléfono:* ${order.client_phone}\n\n` +
      `*Productos:* \n${itemsText}\n\n` +
      `*Total Pagado:* $${order.total_amount.toFixed(2)}\n` +
      `*Método de Entrega:* ${deliveryText}\n\n` +
      `Por favor confirmar despacho de distribuidor. ¡Muchas gracias!`;

    const encoded = encodeURIComponent(message);
    window.open(`https://wa.me/584228098318?text=${encoded}`, "_blank");
  };

  // Wholesale calculator helper
  const calcEstimatedItems = () => {
    const unitsOfCombo = Math.floor(calcBudget / 50);
    const remaining = calcBudget % 50;
    const unitsOfYogurt = Math.floor(remaining / 1.4);
    
    const estimatedCost = calcBudget;
    const estimatedRetailVal = calcBudget * 1.40;
    const estimatedProfit = estimatedRetailVal - estimatedCost;

    return {
      combos: unitsOfCombo,
      yogurts: unitsOfYogurt,
      profit: estimatedProfit,
      margin: 40
    };
  };

  const calcResults = calcEstimatedItems();

  const revealVariants = {
    hidden: { opacity: 0, y: 40 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeOut" } }
  };

  if (isAdminPath) {
    // If the path is /admin or /secreto-admin, we render the dedicated administrative full-page dashboard!
    // No public content is visible.
    if (!user || (user.email !== "admin@nutriyog.com.ve" && user.email !== "DONATEX")) {
      // Show elegant admin login card
      return (
        <div className="min-h-screen bg-[#FAF6EE] flex items-center justify-center p-6 text-left">
          <Toaster position="top-right" richColors />
          <div className="w-full max-w-md bg-white border border-[#F5EFE6] rounded-[2.5rem] shadow-xl p-8 sm:p-10 relative overflow-hidden">
            <div className="absolute top-0 left-0 w-full h-2 bg-[#00BACF]" />
            <div className="text-center mb-8">
              <img src={LOGO_IMAGE_URL} alt="Nutriyog" className="h-20 w-auto mx-auto mb-4 rounded-full border border-[#F5EFE6] bg-white p-1" />
              <h3 className="font-jakarta font-extrabold text-2xl text-slate-900 leading-tight">Panel Administrativo</h3>
              <p className="text-xs text-slate-400 mt-2 uppercase tracking-widest font-bold">Nutre tu día, cuida de ti</p>
            </div>

            <form onSubmit={handleLogin} className="space-y-5">
              <div>
                <label className="text-[10px] font-extrabold text-slate-500 uppercase tracking-widest block mb-1.5">Correo Electrónico</label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <input 
                    type="text"
                    required
                    value={loginForm.email}
                    onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                    className="w-full pl-11 pr-4 py-3 rounded-xl border border-[#D5CBBF] text-sm focus:outline-none focus:ring-2 focus:ring-[#00BACF]"
                    placeholder="Usuario o correo electrónico"
                  />
                </div>
              </div>

              <div>
                <label className="text-[10px] font-extrabold text-slate-500 uppercase tracking-widest block mb-1.5">Contraseña Principal</label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <input 
                    type="password"
                    required
                    value={loginForm.password}
                    onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                    className="w-full pl-11 pr-4 py-3 rounded-xl border border-[#D5CBBF] text-sm focus:outline-none focus:ring-2 focus:ring-[#00BACF]"
                    placeholder="••••••••"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full py-4 rounded-full bg-[#00BACF] hover:bg-cyan-600 disabled:bg-slate-400 text-white font-manrope font-extrabold text-xs tracking-widest uppercase transition-all duration-300 shadow-[0_8px_30px_rgba(0,186,207,0.12)] flex items-center justify-center gap-2 mt-2"
              >
                {loading ? (
                  <span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                ) : (
                  <>
                    <ShieldCheck className="h-4.5 w-4.5" />
                    <span>Ingresar al Sistema</span>
                  </>
                )}
              </button>
            </form>

            <div className="text-center mt-6">
              <a href="/" onClick={() => setIsAdminPath(false)} className="text-xs text-[#00BACF] hover:underline font-bold">Volver al Sitio Público</a>
            </div>
          </div>
        </div>
      );
    }

    // Admin is logged in! Show the full-page administrative experience
    return (
      <div className="min-h-screen bg-[#FDFBF7] font-manrope text-[#2E2825] flex flex-col text-left">
        <Toaster position="top-right" richColors />
        
        {/* Dark Slate Corporate Topbar */}
        <header className="bg-slate-950 text-white h-20 px-6 sm:px-12 flex items-center justify-between shadow-md">
          <div className="flex items-center gap-4">
            <img src={LOGO_IMAGE_URL} alt="Nutriyog" className="h-12 w-auto rounded-full bg-white border border-slate-800 p-0.5" />
            <div>
              <h1 className="font-jakarta font-black text-lg sm:text-xl tracking-tight">Nutriyog</h1>
              <p className="text-[9px] text-[#A69C95] font-extrabold uppercase tracking-widest">Panel de Control de Distribuidor</p>
            </div>
          </div>

          <div className="flex items-center gap-4 sm:gap-6">
            <div className="text-right hidden sm:block">
              <span className="text-[10px] text-[#A69C95] block font-extrabold uppercase tracking-widest">ADMINISTRADOR PRINCIPAL</span>
              <span className="text-sm font-jakarta font-black text-pink-500">¡Hola, Andrea! 🍓🥛</span>
            </div>
            
            <button 
              onClick={handleLogout}
              className="px-5 py-2 rounded-full border border-red-500/30 text-red-400 hover:bg-red-500 hover:text-white font-manrope font-extrabold text-[10px] tracking-wider uppercase transition-all duration-300"
            >
              Cerrar Sesión
            </button>
          </div>
        </header>

        {/* Dashboard Content Grid */}
        <div className="max-w-7xl mx-auto px-6 py-12 grid grid-cols-1 lg:grid-cols-12 gap-12 w-full flex-grow">
          {/* Left Sidebar Menu */}
          <div className="lg:col-span-3 space-y-3">
            <div className="bg-[#FAF6EE] p-5 rounded-3xl border border-[#F5EFE6] space-y-2">
              <span className="text-[9px] text-[#A69C95] font-extrabold uppercase tracking-widest block mb-3">MENÚ DE GESTIÓN</span>
              
              <button 
                onClick={() => setAdminTab("orders")}
                className={`w-full py-3.5 px-4 rounded-xl text-xs font-extrabold uppercase transition-all flex items-center gap-3 ${
                  adminTab === "orders" ? "bg-[#00BACF] text-white shadow-sm font-black" : "text-slate-600 hover:bg-white hover:text-slate-950 border border-transparent hover:border-slate-100"
                }`}
              >
                <ClipboardList className="h-4.5 w-4.5" />
                <span>Ver Pedidos ({allStoreOrders.length})</span>
              </button>

              <button 
                onClick={() => setAdminTab("catalog")}
                className={`w-full py-3.5 px-4 rounded-xl text-xs font-extrabold uppercase transition-all flex items-center gap-3 ${
                  adminTab === "catalog" ? "bg-[#00BACF] text-white shadow-sm font-black" : "text-slate-600 hover:bg-white hover:text-slate-950 border border-transparent hover:border-slate-100"
                }`}
              >
                <Sparkles className="h-4.5 w-4.5" />
                <span>Editar Catálogo</span>
              </button>

              <button 
                onClick={() => setAdminTab("featured")}
                className={`w-full py-3.5 px-4 rounded-xl text-xs font-extrabold uppercase transition-all flex items-center gap-3 ${
                  adminTab === "featured" ? "bg-[#00BACF] text-white shadow-sm font-black" : "text-slate-600 hover:bg-white hover:text-slate-950 border border-transparent hover:border-slate-100"
                }`}
              >
                <Store className="h-4.5 w-4.5" />
                <span>Imagen Destacada</span>
              </button>

              <button 
                onClick={() => setAdminTab("settings")}
                className={`w-full py-3.5 px-4 rounded-xl text-xs font-extrabold uppercase transition-all flex items-center gap-3 ${
                  adminTab === "settings" ? "bg-[#00BACF] text-white shadow-sm font-black" : "text-slate-600 hover:bg-white hover:text-slate-950 border border-transparent hover:border-slate-100"
                }`}
              >
                <Lock className="h-4.5 w-4.5" />
                <span>Ajustes de Tienda</span>
              </button>
            </div>

            <div className="p-4 bg-slate-50 border border-slate-100 rounded-2xl text-center text-[10px] text-slate-400">
              <p className="font-extrabold">ROL ADMINISTRADOR</p>
              <p className="mt-1 font-mono text-[9px] text-slate-500">{user.email}</p>
              <a href="/" onClick={() => setIsAdminPath(false)} className="text-[#00BACF] font-bold block mt-3 hover:underline">Ir a la Web Pública</a>
            </div>
          </div>

          {/* Right Panel Content */}
          <div className="lg:col-span-9 space-y-6">
            
            {/* 1. ORDERS TAB */}
            {adminTab === "orders" && (
              <div className="bg-white border border-[#F5EFE6] rounded-[2rem] p-8 shadow-sm">
                <div className="flex items-center justify-between border-b border-[#F5EFE6] pb-4 mb-6">
                  <div>
                    <h2 className="font-jakarta font-extrabold text-xl text-slate-950">Historial de Ventas Mayoristas</h2>
                    <p className="text-xs text-slate-400 mt-1">Lista en tiempo real de compras realizadas con Stripe y Pago Móvil</p>
                  </div>
                  <span className="text-[10px] bg-green-50 text-green-700 px-3 py-1.5 rounded-full font-extrabold uppercase tracking-wider border border-green-100">
                    Sincronizado
                  </span>
                </div>

                {/* Orders Sub-tabs / Filter */}
                <div className="flex flex-wrap gap-2 mb-6 border-b border-[#F5EFE6] pb-4 text-xs font-bold uppercase tracking-wider">
                  <button 
                    onClick={() => { setOrderFilter("all"); setSelectedOrders([]); }}
                    data-testid="order-tab-all"
                    className={`px-4 py-2 rounded-full transition-all ${
                      orderFilter === "all" ? "bg-[#00BACF] text-white shadow-sm" : "bg-slate-100 text-slate-600 hover:text-slate-900"
                    }`}
                  >
                    Todos ({allStoreOrders.length})
                  </button>
                  <button 
                    onClick={() => { setOrderFilter("processing"); setSelectedOrders([]); }}
                    data-testid="order-tab-processing"
                    className={`px-4 py-2 rounded-full transition-all ${
                      orderFilter === "processing" ? "bg-blue-600 text-white shadow-sm" : "bg-slate-100 text-slate-600 hover:text-slate-900"
                    }`}
                  >
                    En Proceso ({allStoreOrders.filter(o => o.status === "processing" || o.status === "initiated").length})
                  </button>
                  <button 
                    onClick={() => { setOrderFilter("completed"); setSelectedOrders([]); }}
                    data-testid="order-tab-completed"
                    className={`px-4 py-2 rounded-full transition-all ${
                      orderFilter === "completed" ? "bg-emerald-600 text-white shadow-sm" : "bg-slate-100 text-slate-600 hover:text-slate-900"
                    }`}
                  >
                    Aprobados ({allStoreOrders.filter(o => o.status === "completed").length})
                  </button>
                  <button 
                    onClick={() => { setOrderFilter("failed"); setSelectedOrders([]); }}
                    data-testid="order-tab-failed"
                    className={`px-4 py-2 rounded-full transition-all ${
                      orderFilter === "failed" ? "bg-red-600 text-white shadow-sm" : "bg-slate-100 text-slate-600 hover:text-slate-900"
                    }`}
                  >
                    Rechazados ({allStoreOrders.filter(o => o.status === "failed").length})
                  </button>
                </div>

                {/* Bulk Actions floating/top bar */}
                {selectedOrders.length > 0 && (
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-4 mb-6 bg-slate-950 text-white rounded-2xl shadow-md text-xs font-bold animate-fadeIn">
                    <div className="flex items-center gap-2">
                      <span className="bg-[#00BACF] text-white px-2.5 py-1 rounded-full text-[10px] font-black">{selectedOrders.length}</span>
                      <span>Pedidos seleccionados</span>
                    </div>
                    
                    <div className="flex flex-wrap gap-2.5">
                      <button
                        onClick={() => handleBulkUpdateStatus("completed", "paid")}
                        className="py-1.5 px-3 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl uppercase tracking-wider text-[10px] font-extrabold transition-all"
                      >
                        Aprobar
                      </button>
                      <button
                        onClick={() => handleBulkUpdateStatus("failed", "failed")}
                        className="py-1.5 px-3 bg-red-600 hover:bg-red-700 text-white rounded-xl uppercase tracking-wider text-[10px] font-extrabold transition-all"
                      >
                        Rechazar
                      </button>
                      <button
                        onClick={() => handleBulkUpdateStatus("processing", "pending_review")}
                        className="py-1.5 px-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl uppercase tracking-wider text-[10px] font-extrabold transition-all"
                      >
                        En Proceso
                      </button>
                      <button
                        onClick={handleBulkDelete}
                        className="py-1.5 px-3 bg-slate-800 hover:bg-slate-700 text-red-400 hover:text-red-300 rounded-xl uppercase tracking-wider text-[10px] font-extrabold transition-all border border-red-900/30"
                      >
                        Borrar
                      </button>
                      <button
                        onClick={() => setSelectedOrders([])}
                        className="py-1.5 px-3 bg-slate-900 text-slate-400 hover:text-white rounded-xl text-[10px] transition-all"
                      >
                        Cancelar
                      </button>
                    </div>
                  </div>
                )}

                {allStoreOrders.length === 0 ? (
                  <div className="py-20 text-center border-2 border-dashed border-slate-100 rounded-3xl">
                    <ClipboardList className="h-16 w-16 text-slate-200 mx-auto stroke-[1.2] mb-4" />
                    <h4 className="font-jakarta font-bold text-slate-900 text-sm">No hay pedidos registrados</h4>
                    <p className="text-xs text-slate-400 mt-1">Los pedidos aparecerán aquí una vez que los clientes realicen su checkout.</p>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {(() => {
                      const filteredOrders = allStoreOrders.filter(order => {
                        if (orderFilter === "all") return true;
                        if (orderFilter === "processing") return order.status === "processing" || order.status === "initiated";
                        if (orderFilter === "completed") return order.status === "completed";
                        if (orderFilter === "failed") return order.status === "failed";
                        return true;
                      });

                      if (filteredOrders.length === 0) {
                        return (
                          <div className="py-20 text-center border-2 border-dashed border-slate-100 rounded-3xl">
                            <ClipboardList className="h-16 w-16 text-slate-200 mx-auto stroke-[1.2] mb-4" />
                            <h4 className="font-jakarta font-bold text-slate-900 text-sm">No hay pedidos en esta pestaña</h4>
                          </div>
                        );
                      }

                      return filteredOrders.map((order, idx) => (
                        <div key={idx} className="border border-[#F5EFE6] p-6 rounded-3xl bg-white shadow-sm hover:border-[#00BACF]/30 transition-all text-left relative overflow-hidden">
                          {/* Checkbox for Bulk Actions */}
                          <div className="absolute top-4 right-4">
                            <input 
                              type="checkbox"
                              checked={selectedOrders.includes(order.stripe_session_id)}
                              onChange={() => {
                                if (selectedOrders.includes(order.stripe_session_id)) {
                                  setSelectedOrders(selectedOrders.filter(id => id !== order.stripe_session_id));
                                } else {
                                  setSelectedOrders([...selectedOrders, order.stripe_session_id]);
                                }
                              }}
                              className="h-4.5 w-4.5 accent-[#00BACF] cursor-pointer rounded border-slate-300"
                            />
                          </div>

                          <div className="flex flex-col sm:flex-row justify-between items-start gap-4 border-b border-slate-100 pb-4 mb-4 text-xs pr-8">
                            <div>
                              <span className="text-[#A69C95] font-extrabold block uppercase tracking-widest text-[9px]">CLIENTE COMPRADOR:</span>
                              <span className="font-jakarta font-black text-slate-900 text-sm block mt-1">{order.client_name}</span>
                              <span className="font-manrope text-slate-500 block text-[10px] mt-0.5">{order.client_email} • {order.client_phone}</span>
                            </div>
                            <div className="sm:text-right">
                              <span className="text-[#A69C95] font-extrabold block uppercase tracking-widest text-[9px]">REGISTRADO:</span>
                              <span className="font-manrope text-slate-600 block text-xs mt-1 font-bold">{new Date(order.created_at).toLocaleString("es-VE")}</span>
                              <span className="font-mono text-[9px] text-slate-400 block mt-0.5">ID: {order.stripe_session_id.substring(0, 16)}</span>
                            </div>
                          </div>

                          {/* Items Purchased list */}
                          <div className="space-y-2 mb-4 text-xs bg-[#FAF6EE]/50 p-4 rounded-2xl border border-[#F5EFE6]">
                            <span className="text-[#A69C95] font-black block uppercase tracking-widest text-[9px] mb-2">DESGLOSE DE PRODUCTOS:</span>
                            {order.items.map((it, i) => (
                              <div key={i} className="flex justify-between text-slate-700 font-semibold">
                                <span>{it.quantity}x {it.name}</span>
                                <span className="font-black text-slate-900">{formatPrice(it.price * it.quantity)}</span>
                              </div>
                            ))}
                          </div>

                        {/* Delivery detail */}
                        <div className="text-xs text-slate-600 bg-cyan-50/20 p-3 rounded-2xl border border-cyan-100/30 mb-4 flex items-start gap-3">
                          <Truck className="h-5 w-5 text-[#00BACF] flex-shrink-0 mt-0.5" />
                          <div>
                            <span className="text-cyan-800 font-black block text-[9px] uppercase tracking-wider">MÉTODO DE DESPACHO:</span>
                            <p className="mt-1 font-semibold text-slate-700 leading-relaxed">
                              {order.delivery_type === "delivery" ? `Despacho / Delivery a: ${order.delivery_address}` : "Retiro directo en tienda"}
                            </p>
                          </div>
                        </div>

                        <div className="flex justify-between items-center pt-3 border-t border-slate-100 text-xs">
                          <div>
                            <span className={`px-3 py-1.5 rounded-full font-manrope font-extrabold text-[9px] uppercase tracking-wider ${
                              order.payment_status === "paid" ? "bg-green-100 text-green-700 border border-green-200" : "bg-yellow-100 text-yellow-700 border border-yellow-200"
                            }`}>
                              {order.payment_status === "paid" ? "💰 Cobrado Exitosamente" : "⏳ Pago Pendiente"}
                            </span>
                          </div>
                          <span className="font-jakarta font-black text-base text-pink-600">
                            Monto Total: {formatPrice(order.total_amount)}
                          </span>
                        </div>

                        {/* Pago Móvil Specific details */}
                        {order.payment_method === "pago_movil" && order.payment_details && (
                          <div className="text-xs text-slate-700 bg-emerald-50 p-4 rounded-2xl border border-emerald-100/50 mb-4 space-y-2 mt-4">
                            <span className="text-emerald-800 font-extrabold block text-[9px] uppercase tracking-wider">Detalles del Pago Móvil (Por Validar):</span>
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 font-semibold mt-1">
                              <div>
                                <span className="text-[10px] text-slate-400 block uppercase">Banco / Teléfono Emisor:</span>
                                <span className="text-slate-800 text-xs block font-bold">{order.payment_details.bank_phone}</span>
                              </div>
                              <div>
                                <span className="text-[10px] text-slate-400 block uppercase">Número de Referencia:</span>
                                <span className="text-[#00BACF] text-xs block font-black font-mono">{order.payment_details.reference}</span>
                              </div>
                              <div>
                                <span className="text-[10px] text-slate-400 block uppercase">Monto Reportado:</span>
                                <span className="text-pink-600 text-xs block font-black">{order.payment_details.amount_paid}</span>
                              </div>
                            </div>
                          </div>
                        )}

                        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center pt-4 mt-4 border-t border-slate-100 gap-4">
                          <div className="flex items-center gap-3">
                            <span className={`px-3 py-1.5 rounded-full font-manrope font-extrabold text-[9px] uppercase tracking-wider ${
                              order.payment_status === "paid" ? "bg-green-100 text-green-700 border border-green-200" : "bg-yellow-100 text-yellow-700 border border-yellow-200"
                            }`}>
                              {order.payment_method === "pago_movil" ? "📱 Pago Móvil" : "💳 Stripe"} • {order.payment_status === "paid" ? "Cobrado" : "Pendiente de Revisión"}
                            </span>
                            
                            <span className={`px-3 py-1.5 rounded-full font-manrope font-extrabold text-[9px] uppercase tracking-wider ${
                              order.status === "completed" ? "bg-emerald-100 text-emerald-800 border border-emerald-200" : order.status === "failed" ? "bg-red-100 text-red-800 border border-red-200" : "bg-blue-100 text-blue-800 border border-blue-200"
                            }`}>
                              {order.status === "completed" ? "Aprobado" : order.status === "failed" ? "Rechazado" : "En Proceso"}
                            </span>
                          </div>
                          
                          <span className="font-jakarta font-black text-sm text-pink-600">
                            Monto Total: {formatPrice(order.total_amount)}
                          </span>
                        </div>

                        {/* Order Management Actions */}
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 pt-4 mt-4 border-t border-dashed border-slate-200">
                          <button
                            onClick={() => handleUpdateOrderStatus(order.stripe_session_id, "completed", "paid")}
                            className="py-2 px-3 bg-emerald-600 hover:bg-emerald-700 text-white font-manrope font-extrabold text-[10px] uppercase tracking-wider rounded-xl transition-all text-center flex items-center justify-center gap-1.5"
                          >
                            <Check className="h-3.5 w-3.5" />
                            <span>Aprobar</span>
                          </button>
                          
                          <button
                            onClick={() => handleUpdateOrderStatus(order.stripe_session_id, "failed", "failed")}
                            className="py-2 px-3 bg-red-600 hover:bg-red-700 text-white font-manrope font-extrabold text-[10px] uppercase tracking-wider rounded-xl transition-all text-center flex items-center justify-center gap-1.5"
                          >
                            <X className="h-3.5 w-3.5" />
                            <span>Rechazar</span>
                          </button>
                          
                          <button
                            onClick={() => handleUpdateOrderStatus(order.stripe_session_id, "processing", "pending_review")}
                            className="py-2 px-3 bg-blue-600 hover:bg-blue-700 text-white font-manrope font-extrabold text-[10px] uppercase tracking-wider rounded-xl transition-all text-center flex items-center justify-center gap-1.5"
                          >
                            <Sparkles className="h-3.5 w-3.5" />
                            <span>En Proceso</span>
                          </button>

                          <button
                            onClick={() => handleDeleteOrder(order.stripe_session_id)}
                            className="py-2 px-3 bg-slate-700 hover:bg-slate-800 text-white font-manrope font-extrabold text-[10px] uppercase tracking-wider rounded-xl transition-all text-center flex items-center justify-center gap-1.5"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                            <span>Borrar</span>
                          </button>
                        </div>
                      </div>
                    ));
                  })()}
                  </div>
                )}
              </div>
            )}

            {/* 2. CATALOG TAB */}
            {adminTab === "catalog" && (
              <div className="bg-white border border-[#F5EFE6] rounded-[2rem] p-8 shadow-sm">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between border-b border-[#F5EFE6] pb-4 mb-6 gap-4 text-left">
                  <div>
                    <h2 className="font-jakarta font-extrabold text-xl text-slate-950">Administrador de Productos en Venta</h2>
                    <p className="text-xs text-slate-400 mt-1">Edita nombres, precios, fotos y límites de compra al instante</p>
                  </div>
                  
                  <button
                    onClick={() => setShowAddProductForm(!showAddProductForm)}
                    className="px-5 py-2.5 rounded-xl bg-[#00BACF] hover:bg-cyan-600 text-white font-manrope font-extrabold text-xs uppercase tracking-wider transition-all shadow-sm w-fit"
                  >
                    {showAddProductForm ? "✕ Cerrar Formulario" : "＋ Crear Nuevo Producto o Combo (Campaña)"}
                  </button>
                </div>

                {showAddProductForm && (
                  <form onSubmit={handleCreateProductCMS} className="border-2 border-dashed border-[#00BACF]/50 rounded-3xl p-6 sm:p-8 bg-white text-xs text-left space-y-5 relative overflow-hidden shadow-md mb-8">
                    <div className="absolute top-0 left-0 w-2.5 h-full bg-[#00BACF]" />
                    <h5 className="font-jakarta font-black text-slate-800 text-base border-b pb-3 flex items-center gap-2">
                      <Sparkles className="h-5 w-5 text-pink-500 animate-pulse" />
                      <span>Crear Nueva Campaña / Combo de Productos</span>
                    </h5>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div>
                        <label className="text-[9px] font-extrabold text-slate-500 uppercase tracking-widest block mb-1.5">ID Único (Ej: combo_fresa_mini) *</label>
                        <input 
                          type="text"
                          required
                          placeholder="Ej: combo_fresa_mini"
                          value={newProductForm.id}
                          onChange={(e) => setNewProductForm({ ...newProductForm, id: e.target.value })}
                          className="w-full px-4 py-2.5 border border-[#D5CBBF] rounded-xl bg-[#FAF6EE]/20 text-xs font-mono font-bold focus:outline-none"
                        />
                      </div>
                      <div>
                        <label className="text-[9px] font-extrabold text-slate-500 uppercase tracking-widest block mb-1.5">Nombre en Tienda *</label>
                        <input 
                          type="text"
                          required
                          placeholder="Ej. Combo Fresa Mini"
                          value={newProductForm.name}
                          onChange={(e) => setNewProductForm({ ...newProductForm, name: e.target.value })}
                          className="w-full px-4 py-2.5 border border-[#D5CBBF] rounded-xl bg-white text-xs font-bold focus:outline-none"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div>
                        <label className="text-[9px] font-extrabold text-slate-500 uppercase tracking-widest block mb-1.5">Precio de Fábrica ($ USD) *</label>
                        <input 
                          type="number"
                          step="0.01"
                          required
                          placeholder="Ej: 45.00"
                          value={newProductForm.price}
                          onChange={(e) => setNewProductForm({ ...newProductForm, price: e.target.value })}
                          className="w-full px-4 py-2.5 border border-[#D5CBBF] rounded-xl bg-white text-xs font-black text-pink-600 focus:outline-none"
                        />
                      </div>
                      <div>
                        <label className="text-[9px] font-extrabold text-slate-500 uppercase tracking-widest block mb-1.5">Tamaño / Envase</label>
                        <input 
                          type="text"
                          placeholder="Ej. 12 Vasos de 200ml"
                          value={newProductForm.size}
                          onChange={(e) => setNewProductForm({ ...newProductForm, size: e.target.value })}
                          className="w-full px-4 py-2.5 border border-[#D5CBBF] rounded-xl bg-white text-xs font-bold focus:outline-none"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div>
                        <label className="text-[9px] font-extrabold text-slate-500 uppercase tracking-widest block mb-1.5">Etiqueta (Badge)</label>
                        <input 
                          type="text"
                          placeholder="Ej. Campaña, Especial, Lanzamiento"
                          value={newProductForm.badge}
                          onChange={(e) => setNewProductForm({ ...newProductForm, badge: e.target.value })}
                          className="w-full px-4 py-2.5 border border-[#D5CBBF] rounded-xl bg-white text-xs font-extrabold focus:outline-none"
                        />
                      </div>
                      <div>
                        <label className="text-[9px] font-extrabold text-slate-500 uppercase tracking-widest block mb-1.5">Pedido Mínimo (Uds)</label>
                        <input 
                          type="number"
                          value={newProductForm.min_qty}
                          onChange={(e) => setNewProductForm({ ...newProductForm, min_qty: e.target.value })}
                          className="w-full px-4 py-2.5 border border-[#D5CBBF] rounded-xl bg-white text-xs font-bold focus:outline-none"
                        />
                      </div>
                    </div>

                    {/* Image Uploader widget for new product */}
                    <div className="p-4 bg-[#FAF6EE] rounded-2xl border border-[#F5EFE6] space-y-3">
                      <label className="text-[9px] font-extrabold text-slate-500 uppercase tracking-widest block">Subir Imagen del Producto</label>
                      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
                        <input 
                          type="file"
                          accept="image/*"
                          onChange={(e) => {
                            const file = e.target.files[0];
                            if (file) {
                              const formData = new FormData();
                              formData.append("file", file);
                              setLoading(true);
                              axios.post(`${API}/upload`, formData, {
                                headers: { "Content-Type": "multipart/form-data" }
                              }).then(res => {
                                const uploadedUrl = `${BACKEND_URL}${res.data.url}`;
                                setNewProductForm({ ...newProductForm, image: uploadedUrl });
                                toast.success("¡Imagen subida con éxito!");
                              }).catch(err => {
                                console.error(err);
                                toast.error("Error al subir la imagen.");
                              }).finally(() => setLoading(false));
                            }
                          }}
                          className="block w-full text-xs text-slate-500 file:mr-4 file:py-2.5 file:px-4 file:rounded-xl file:border-0 file:text-xs file:font-extrabold file:bg-pink-50 file:text-pink-700 hover:file:bg-pink-100 cursor-pointer"
                        />
                      </div>
                      {newProductForm.image && (
                        <div className="mt-2 text-[10px] text-slate-400 font-mono truncate">
                          <span className="font-extrabold text-slate-500 mr-2 uppercase text-[9px]">Imagen:</span>
                          {newProductForm.image}
                        </div>
                      )}
                    </div>

                    <div>
                      <label className="text-[9px] font-extrabold text-slate-500 uppercase tracking-widest block mb-1.5">Descripción para Clientes</label>
                      <textarea 
                        value={newProductForm.description}
                        rows={3}
                        onChange={(e) => setNewProductForm({ ...newProductForm, description: e.target.value })}
                        className="w-full px-4 py-2.5 border border-[#D5CBBF] rounded-xl bg-white text-xs text-slate-600 leading-relaxed font-manrope focus:outline-none"
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full py-4 rounded-2xl bg-green-600 hover:bg-green-700 text-white font-manrope font-extrabold text-xs uppercase tracking-widest transition-all flex items-center justify-center gap-2 shadow-sm"
                    >
                      <Check className="h-5 w-5" />
                      <span>Crear Campaña / Combo de Productos</span>
                    </button>
                  </form>
                )}

                <div className="space-y-8">
                  {products.map((product) => {
                    const editForm = editingProductsList[product.id] || product;

                    return (
                      <div key={product.id} className="border border-[#F5EFE6] rounded-3xl p-6 sm:p-8 bg-[#FAF6EE]/30 text-xs text-left space-y-5 relative overflow-hidden shadow-sm hover:shadow-md transition-all">
                        <div className="absolute top-0 left-0 w-2.5 h-full bg-[#00BACF]" />
                        
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-slate-100 pb-4">
                          <div className="flex items-center gap-4">
                            <img src={editForm.image} alt={editForm.name} className="w-16 h-16 rounded-2xl object-cover border border-[#F5EFE6] bg-white shadow-sm" />
                            <div>
                              <span className="text-[9px] text-[#A69C95] font-black uppercase tracking-wider bg-slate-100 px-2 py-0.5 rounded-md">ID: {product.id}</span>
                              <h5 className="font-jakarta font-black text-slate-800 text-base mt-1.5">{product.name}</h5>
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-2">
                            <span className="text-[10px] text-[#00BACF] bg-white px-3 py-1 rounded-full font-black tracking-widest border uppercase w-fit">
                              Activo
                            </span>
                            
                            <button
                              onClick={() => handleDeleteProductCMS(product.id, product.name)}
                              className="p-2 bg-red-50 hover:bg-red-100 text-red-500 rounded-xl transition-all border border-red-100"
                              title="Eliminar del catálogo"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                          <div>
                            <label className="text-[9px] font-extrabold text-slate-500 uppercase tracking-widest block mb-1.5">Nombre en Tienda</label>
                            <input 
                              type="text"
                              value={editForm.name || ""}
                              onChange={(e) => handleUpdateProductField(product.id, "name", e.target.value)}
                              className="w-full px-4 py-2.5 border border-[#D5CBBF] rounded-xl bg-white text-xs font-bold focus:ring-1 focus:ring-[#00BACF] focus:outline-none"
                            />
                          </div>
                          <div>
                            <label className="text-[9px] font-extrabold text-slate-500 uppercase tracking-widest block mb-1.5">Precio de Fábrica ($ USD)</label>
                            <input 
                              type="number"
                              step="0.01"
                              value={editForm.price || 0}
                              onChange={(e) => handleUpdateProductField(product.id, "price", e.target.value)}
                              className="w-full px-4 py-2.5 border border-[#D5CBBF] rounded-xl bg-white text-xs font-black text-pink-600 focus:ring-1 focus:ring-[#00BACF] focus:outline-none"
                            />
                          </div>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                          <div>
                            <label className="text-[9px] font-extrabold text-slate-500 uppercase tracking-widest block mb-1.5">Tamaño / Envase</label>
                            <input 
                              type="text"
                              value={editForm.size || ""}
                              onChange={(e) => handleUpdateProductField(product.id, "size", e.target.value)}
                              className="w-full px-4 py-2.5 border border-[#D5CBBF] rounded-xl bg-white text-xs font-bold focus:ring-1 focus:ring-[#00BACF] focus:outline-none"
                            />
                          </div>
                          <div>
                            <label className="text-[9px] font-extrabold text-slate-500 uppercase tracking-widest block mb-1.5">Etiqueta (Badge)</label>
                            <input 
                              type="text"
                              value={editForm.badge || ""}
                              onChange={(e) => handleUpdateProductField(product.id, "badge", e.target.value)}
                              className="w-full px-4 py-2.5 border border-[#D5CBBF] rounded-xl bg-white text-xs font-extrabold focus:ring-1 focus:ring-[#00BACF] focus:outline-none"
                            />
                          </div>
                          <div>
                            <label className="text-[9px] font-extrabold text-slate-500 uppercase tracking-widest block mb-1.5">Pedido Mínimo (Uds)</label>
                            <input 
                              type="number"
                              value={editForm.min_qty || 1}
                              onChange={(e) => handleUpdateProductField(product.id, "min_qty", e.target.value)}
                              className="w-full px-4 py-2.5 border border-[#D5CBBF] rounded-xl bg-white text-xs font-bold focus:ring-1 focus:ring-[#00BACF] focus:outline-none"
                            />
                          </div>
                        </div>

                        {/* Image Uploader widget instead of static text link */}
                        <div className="p-4 bg-white rounded-2xl border border-[#F5EFE6] space-y-3">
                          <label className="text-[9px] font-extrabold text-slate-500 uppercase tracking-widest block">Subir Imagen del Producto</label>
                          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
                            <input 
                              type="file"
                              accept="image/*"
                              onChange={(e) => handleImageUploadCMS(product.id, e.target.files[0])}
                              className="block w-full text-xs text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-xl file:border-0 file:text-xs file:font-extrabold file:bg-pink-50 file:text-pink-700 hover:file:bg-pink-100 cursor-pointer"
                            />
                          </div>
                          {editForm.image && (
                            <div className="mt-2 flex items-center gap-2 text-[10px] text-slate-400 font-mono overflow-hidden">
                              <span className="font-extrabold text-slate-500 uppercase text-[9px] tracking-wider flex-shrink-0">Imagen actual:</span>
                              <span className="truncate">{editForm.image}</span>
                            </div>
                          )}
                        </div>

                        <div>
                          <label className="text-[9px] font-extrabold text-slate-500 uppercase tracking-widest block mb-1.5">Descripción para Clientes</label>
                          <textarea 
                            value={editForm.description || ""}
                            rows={3}
                            onChange={(e) => handleUpdateProductField(product.id, "description", e.target.value)}
                            className="w-full px-4 py-2.5 border border-[#D5CBBF] rounded-xl bg-white text-xs text-slate-600 leading-relaxed font-manrope focus:ring-1 focus:ring-[#00BACF] focus:outline-none"
                          />
                        </div>

                        <button
                          onClick={() => handleSaveProductCMS(product.id)}
                          disabled={loading}
                          data-testid={`cms-save-${product.id}`}
                          className="w-full py-4 rounded-2xl bg-green-600 hover:bg-green-700 text-white font-manrope font-extrabold text-xs uppercase tracking-widest transition-all flex items-center justify-center gap-2 shadow-sm"
                        >
                          <Check className="h-5 w-5" />
                          <span>Guardar Cambios de {product.name}</span>
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* 3. FEATURED IMAGE TAB */}
            {adminTab === "featured" && (
              <div className="bg-white border border-[#F5EFE6] rounded-[2rem] p-8 shadow-sm text-left space-y-6">
                <div className="border-b border-[#F5EFE6] pb-4">
                  <h2 className="font-jakarta font-extrabold text-xl text-slate-950">Imagen Destacada Principal (Flyer)</h2>
                  <p className="text-xs text-slate-400 mt-1">Esta imagen es la pieza principal (Flyer de marca) que se muestra en el inicio y destaca tu negocio</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-center bg-[#FAF6EE]/50 p-6 rounded-3xl border border-[#F5EFE6]">
                  {/* Current Preview */}
                  <div className="md:col-span-4 flex justify-center">
                    <div className="relative max-w-[180px] aspect-[3/4] rounded-2xl bg-slate-100 border p-2 shadow-sm overflow-hidden">
                      <img src={homepageSettings.featured_image} alt="Featured Flyer" className="w-full h-full object-cover rounded-xl" />
                    </div>
                  </div>

                  {/* Uploader */}
                  <div className="md:col-span-8 space-y-4">
                    <span className="text-[10px] text-[#A69C95] font-black uppercase tracking-widest block">Subir Nuevo Flyer / Imagen Destacada</span>
                    <p className="text-xs text-slate-600 leading-relaxed">
                      Selecciona un archivo JPG, PNG o WebP de alta calidad desde tu computadora. El sistema lo guardará en tu servidor y actualizará la página web de inmediato.
                    </p>
                    
                    <input 
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleFeaturedImageUploadCMS(e.target.files[0])}
                      className="block w-full text-xs text-slate-500 file:mr-4 file:py-2.5 file:px-4 file:rounded-xl file:border-0 file:text-xs file:font-extrabold file:bg-pink-50 file:text-pink-700 hover:file:bg-pink-100 cursor-pointer"
                    />

                    <div className="text-[9px] text-slate-400 font-mono truncate">
                      <span className="font-bold uppercase text-slate-500 mr-2">Enlace actual:</span>
                      {homepageSettings.featured_image}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* 4. SETTINGS TAB */}
            {adminTab === "settings" && (
              <div className="bg-white border border-[#F5EFE6] rounded-[2rem] p-8 shadow-sm text-left space-y-8">
                <div className="border-b border-[#F5EFE6] pb-4">
                  <h2 className="font-jakarta font-extrabold text-xl text-slate-950">Ajustes Generales de la Tienda</h2>
                  <p className="text-xs text-slate-400 mt-1">Configura la tasa de cambio manual en Bolívares y administra los métodos de pago activos</p>
                </div>

                {/* Exchange Rate Section */}
                <div className="bg-[#FAF6EE]/50 p-6 rounded-3xl border border-[#F5EFE6] space-y-4">
                  <div className="flex items-center gap-3 border-b border-slate-100 pb-3">
                    <TrendingUp className="h-5 w-5 text-pink-500" />
                    <h3 className="font-jakarta font-black text-sm text-slate-950 uppercase tracking-wider">Ajuste Manual de Tasa de Cambio (VES)</h3>
                  </div>

                  <p className="text-xs text-slate-600 leading-relaxed font-semibold">
                    Configura la tasa manual del dólar (VES) en bolívares. Esta tasa convertirá automáticamente todos los precios en la web cuando el usuario active el selector de VES.
                  </p>

                  <div className="flex flex-col sm:flex-row items-end gap-4 max-w-md">
                    <div className="flex-grow">
                      <label className="text-[9px] font-extrabold text-slate-500 uppercase tracking-widest block mb-1">Tasa de Cambio Manual (Bs. / 1 USD)</label>
                      <input 
                        type="number"
                        step="0.01"
                        value={inputExchangeRate}
                        onChange={(e) => setInputExchangeRate(e.target.value)}
                        className="w-full px-4 py-2.5 border border-[#D5CBBF] rounded-xl bg-white text-xs font-black text-pink-600 focus:outline-none"
                      />
                    </div>
                    <button
                      onClick={handleSaveExchangeRateCMS}
                      disabled={loading}
                      className="py-3 px-6 bg-pink-500 hover:bg-pink-600 text-white rounded-full font-manrope font-extrabold text-xs uppercase tracking-wider transition-all shadow-sm"
                    >
                      Guardar Tasa
                    </button>
                  </div>
                </div>

                {/* Payment Methods Section */}
                <div className="bg-[#FAF6EE]/50 p-6 rounded-3xl border border-[#F5EFE6] space-y-6">
                  <div className="flex items-center gap-3 border-b border-slate-100 pb-3">
                    <ShieldCheck className="h-5 w-5 text-pink-500" />
                    <h3 className="font-jakarta font-black text-sm text-slate-950 uppercase tracking-wider">Métodos de Pago Activos (Encender / Apagar)</h3>
                  </div>

                  <div className="space-y-6">
                    {/* Stripe Trigger */}
                    <div className="flex items-start justify-between p-4 bg-white rounded-2xl border border-slate-100 shadow-sm">
                      <div>
                        <span className="font-jakarta font-extrabold text-xs block text-slate-900">Pasarela Stripe (Tarjetas Internacionales)</span>
                        <span className="text-[10px] text-slate-400 block mt-0.5">Habilita o deshabilita los cobros con tarjeta en línea</span>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer mt-1">
                        <input 
                          type="checkbox" 
                          checked={paymentMethods.stripe?.enabled || false}
                          onChange={(e) => setPaymentMethods({
                            ...paymentMethods,
                            stripe: { ...paymentMethods.stripe, enabled: e.target.checked }
                          })}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#00BACF]"></div>
                      </label>
                    </div>

                    {/* Pago Móvil Trigger */}
                    <div className="flex items-start justify-between p-4 bg-white rounded-2xl border border-slate-100 shadow-sm">
                      <div>
                        <span className="font-jakarta font-extrabold text-xs block text-slate-900">Pago Móvil (Manual en Bolívares)</span>
                        <span className="text-[10px] text-slate-400 block mt-0.5">Habilita o deshabilita la transferencia manual local</span>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer mt-1">
                        <input 
                          type="checkbox" 
                          checked={paymentMethods.pago_movil?.enabled || false}
                          onChange={(e) => setPaymentMethods({
                            ...paymentMethods,
                            pago_movil: { ...paymentMethods.pago_movil, enabled: e.target.checked }
                          })}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#00BACF]"></div>
                      </label>
                    </div>

                    {/* Edit Pago Móvil details */}
                    {paymentMethods.pago_movil?.enabled && (
                      <div className="p-4 bg-white rounded-2xl border border-slate-100 shadow-sm space-y-2">
                        <label className="text-[9px] font-extrabold text-slate-500 uppercase tracking-widest block">Instrucciones de Pago Móvil para Clientes</label>
                        <textarea 
                          value={paymentMethods.pago_movil?.details || ""}
                          rows={3}
                          onChange={(e) => setPaymentMethods({
                            ...paymentMethods,
                            pago_movil: { ...paymentMethods.pago_movil, details: e.target.value }
                          })}
                          className="w-full px-4 py-2.5 border border-[#D5CBBF] rounded-xl bg-[#FAF6EE]/20 text-xs text-slate-600 leading-relaxed font-manrope focus:outline-none focus:ring-1 focus:ring-[#00BACF]"
                          placeholder="Banco Banesco • Teléfono: 0422... • C.I: ..."
                        />
                      </div>
                    )}
                  </div>

                  <button
                    onClick={handleSavePaymentMethodsCMS}
                    disabled={loading}
                    className="w-full py-4 rounded-2xl bg-green-600 hover:bg-green-700 text-white font-manrope font-extrabold text-xs uppercase tracking-widest transition-all flex items-center justify-center gap-2 shadow-sm"
                  >
                    <Check className="h-5 w-5" />
                    <span>Guardar Métodos de Pago Activos</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7] font-manrope text-[#2E2825] selection:bg-pink-500 selection:text-white relative overflow-x-hidden">
      <Toaster position="top-right" richColors />

      {/* --- Announcement Bar (Highlight Wholesale & Promos with Turquoise color) --- */}
      <div className="w-full bg-[#00BACF] text-white py-2.5 text-center text-xs font-bold tracking-wider uppercase flex flex-col sm:flex-row items-center justify-center gap-2 px-6">
        <div className="flex items-center gap-2">
          <Sparkles className="h-4 w-4 animate-bounce" />
          <span>🔥 DISTRIBUIDORES MAYORISTAS: Precios al Mayor Directo de Fábrica • Combos para Emprendedores desde $50.00 • ¡Envío Gratis &gt; $30!</span>
        </div>
        {currency === "VES" && (
          <span className="sm:border-l sm:pl-3 border-white/50 text-[10px] bg-white/10 px-2.5 py-0.5 rounded-full font-black">
            📈 Tasa Oficial BCV: 1 USD = {exchangeRate.toFixed(2)} Bs.
          </span>
        )}
      </div>

      {/* --- Elegant Header --- */}
      <header className="sticky top-0 z-40 w-full bg-[#FDFBF7]/90 backdrop-blur-xl border-b border-[#F5EFE6] transition-all duration-300">
        <div className="max-w-7xl mx-auto px-6 h-20 sm:h-24 flex items-center justify-between">
          <a href="#" data-testid="logo-link" className="flex items-center gap-2 transform hover:scale-[1.01] transition-transform duration-300">
            <img src={LOGO_IMAGE_URL} alt="Nutriyog Logo" className="h-12 sm:h-16 w-auto" />
          </a>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-10 font-manrope font-semibold text-xs tracking-[0.15em] uppercase text-[#544B45]">
            <a href="#" data-testid="nav-link-home" className="hover:text-pink-600 transition-colors duration-300">Inicio</a>
            <a href="#productos" data-testid="nav-link-products" className="hover:text-pink-600 transition-colors duration-300">Precios al Mayor</a>
            <a href="#calculadora" className="hover:text-pink-600 transition-colors duration-300">Calculadora de Ganancia</a>
            <a href="#manifiesto" data-testid="nav-link-manifesto" className="hover:text-pink-600 transition-colors duration-300">Historia</a>
          </nav>

          {/* User actions & Cart */}
          <div className="flex items-center gap-3 sm:gap-4">
            {/* Currency Selector Pill (Hidden on Mobile) */}
            <div className="hidden md:flex items-center bg-[#FAF6EE] border border-[#D5CBBF] rounded-full p-1 shadow-sm">
              <button
                onClick={() => setCurrency("USD")}
                data-testid="currency-toggle-usd"
                className={`px-3 py-1 rounded-full text-[10px] font-extrabold uppercase transition-all duration-300 ${
                  currency === "USD" ? "bg-[#00BACF] text-white shadow-sm" : "text-[#544B45] hover:text-[#00BACF]"
                }`}
                title="Mostrar precios en Dólares"
              >
                USD
              </button>
              <button
                onClick={() => setCurrency("VES")}
                data-testid="currency-toggle-ves"
                className={`px-3 py-1 rounded-full text-[10px] font-extrabold uppercase transition-all duration-300 ${
                  currency === "VES" ? "bg-[#00BACF] text-white shadow-sm" : "text-[#544B45] hover:text-[#00BACF]"
                }`}
                title="Mostrar precios en Bolívares (BCV)"
              >
                VES
              </button>
            </div>
            {user ? (
              <div className="flex items-center gap-2">
                <button 
                  onClick={() => setIsProfileOpen(true)}
                  data-testid="user-profile-trigger"
                  className="flex items-center gap-2 px-4 py-2.5 rounded-full bg-cyan-50 border border-cyan-100 text-cyan-700 font-bold text-xs tracking-wider uppercase hover:bg-cyan-100 transition-all duration-300"
                >
                  <User className="h-4 w-4 text-[#00BACF]" />
                  <span className="hidden sm:inline">Hola, {user.first_name}</span>
                </button>
                <button 
                  onClick={handleLogout}
                  data-testid="auth-logout-button"
                  className="p-2.5 rounded-full hover:bg-red-50 text-red-500 transition-all"
                  title="Cerrar Sesión"
                >
                  <LogOut className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <button 
                onClick={() => setAuthModal("login")}
                data-testid="auth-login-trigger"
                className="px-3 sm:px-5 py-2.5 rounded-full border border-[#D5CBBF] text-[#544B45] hover:border-pink-500 hover:text-pink-600 font-bold text-xs tracking-wider uppercase transition-all duration-300 flex items-center gap-2"
              >
                <User className="h-4 w-4 flex-shrink-0" />
                <span className="hidden sm:inline">Iniciar Sesión</span>
              </button>
            )}

            <button 
              onClick={() => setIsCartOpen(true)}
              data-testid="cart-toggle-button"
              className="relative p-3 rounded-full bg-pink-50 hover:bg-pink-100 text-pink-600 font-bold transition-all duration-300 flex items-center gap-2 shadow-sm border border-pink-100 hover:-translate-y-[1px]"
            >
              <ShoppingBag className="h-5 w-5" />
              <span className="text-xs bg-pink-500 text-white rounded-full px-2 py-0.5 min-w-[18px] text-center inline-block">
                {getCartTotalItems()}
              </span>
            </button>

            <button 
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              data-testid="mobile-menu-toggle"
              className="lg:hidden p-2 rounded-full hover:bg-slate-100 transition-all text-slate-800"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {isMobileMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        </div>

        {/* Mobile Nav */}
        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div 
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="lg:hidden bg-[#FDFBF7] border-t border-[#F5EFE6]"
            >
              <div className="flex flex-col p-6 gap-3 font-manrope font-semibold uppercase tracking-widest text-xs text-[#544B45]">
                <a href="#" onClick={() => setIsMobileMenuOpen(false)} className="hover:text-pink-600 p-2">Inicio</a>
                <a href="#productos" onClick={() => setIsMobileMenuOpen(false)} className="hover:text-pink-600 p-2">Precios al Mayor</a>
                <a href="#calculadora" onClick={() => setIsMobileMenuOpen(false)} className="hover:text-pink-600 p-2">Calculadora de Ganancia</a>
                <a href="#manifiesto" onClick={() => setIsMobileMenuOpen(false)} className="hover:text-pink-600 p-2">Historia</a>

                {/* Mobile Currency Selector Pill */}
                <div className="flex flex-col gap-2.5 mt-4 pt-4 border-t border-[#F5EFE6] text-left">
                  <span className="text-[10px] text-[#A69C95] font-black uppercase tracking-widest pl-2">Seleccionar Moneda:</span>
                  <div className="flex bg-[#FAF6EE] border border-[#D5CBBF] rounded-full p-1 shadow-sm max-w-[200px]">
                    <button
                      onClick={() => setCurrency("USD")}
                      className={`flex-grow py-2.5 rounded-full text-xs font-extrabold uppercase transition-all duration-300 ${
                        currency === "USD" ? "bg-[#00BACF] text-white shadow-sm" : "text-[#544B45] hover:text-[#00BACF]"
                      }`}
                    >
                      USD ($)
                    </button>
                    <button
                      onClick={() => setCurrency("VES")}
                      className={`flex-grow py-2.5 rounded-full text-xs font-extrabold uppercase transition-all duration-300 ${
                        currency === "VES" ? "bg-[#00BACF] text-white shadow-sm" : "text-[#544B45] hover:text-[#00BACF]"
                      }`}
                    >
                      VES (Bs.)
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      {/* --- Editorial Hero Section --- */}
      <section className="relative min-h-[90vh] flex items-center pt-8 pb-20 bg-gradient-to-b from-[#F9F5EA] to-[#FDFBF7] overflow-hidden text-left">
        {/* Decorative soft circles with turquoise brand tone */}
        <div className="absolute top-20 left-10 w-36 h-36 bg-pink-100 rounded-full mix-blend-multiply filter blur-2xl opacity-20 animate-pulse-soft" />
        <div className="absolute bottom-10 right-10 w-44 h-44 bg-cyan-100 rounded-full mix-blend-multiply filter blur-2xl opacity-35 animate-pulse" />
        
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-12 gap-16 items-center relative z-10 w-full">
          {/* Hero text */}
          <div className="lg:col-span-7 flex flex-col items-start">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="mb-4 inline-flex items-center gap-2 bg-pink-50 border border-pink-100 text-pink-600 rounded-full px-5 py-2 text-[10px] uppercase tracking-[0.2em] font-extrabold"
            >
              <Store className="h-4.5 w-4.5" />
              <span>Oportunidad de Negocio • Despacho Mayorista</span>
            </motion.div>

            <motion.h1 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-5xl sm:text-6xl lg:text-7xl font-jakarta font-extrabold text-[#1F1815] leading-[1.08] tracking-tight"
            >
              Yogurt al Mayor: <br />
              <span className="italic font-normal text-pink-500 bg-gradient-to-r from-pink-500 to-pink-600 bg-clip-text text-transparent">Emprende con nosotros.</span>
            </motion.h1>

            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="mt-6 text-base sm:text-lg text-[#544B45] max-w-xl font-manrope leading-relaxed"
            >
              Somos productores y distribuidores mayoristas de la línea premium de yogurt líquido y firme en Venezuela. Ofrecemos precios de fábrica y promociones exclusivas para emprendedores y comercios locales.
            </motion.p>

            {/* Feature lists with turquoise checks */}
            <div className="mt-6 space-y-3 font-manrope text-sm font-semibold text-[#544B45]">
              <div className="flex items-center gap-3">
                <div className="p-1 rounded-full bg-cyan-100 text-[#00BACF]"><Check className="h-4 w-4 stroke-[3]" /></div>
                <span>Precios al mayor en todas las presentaciones (200ml, 400ml, 900ml, 500g firme).</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="p-1 rounded-full bg-cyan-100 text-[#00BACF]"><Check className="h-4 w-4 stroke-[3]" /></div>
                <span>Margen de ganancia excelente de hasta el 40% para revendedores.</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="p-1 rounded-full bg-cyan-100 text-[#00BACF]"><Check className="h-4 w-4 stroke-[3]" /></div>
                <span>Combos especiales diseñados para arrancar negocios con solo $50.00.</span>
              </div>
            </div>

            {/* CTAs */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.5 }}
              className="mt-10 flex flex-wrap gap-4"
            >
              <a 
                href="#productos"
                data-testid="hero-primary-cta"
                className="px-8 py-4.5 rounded-full bg-pink-500 hover:bg-pink-600 text-white font-manrope font-bold text-xs tracking-wider uppercase transition-all duration-300 shadow-[0_8px_30px_rgba(255,0,127,0.12)] hover:-translate-y-0.5 inline-flex items-center gap-3"
              >
                <span>Hacer Pedido al Mayor</span>
                <ArrowRight className="h-4 w-4" />
              </a>
              <a 
                href="#calculadora"
                className="px-8 py-4.5 rounded-full bg-[#00BACF] text-white hover:bg-cyan-600 font-manrope font-bold text-xs tracking-wider uppercase transition-all duration-300 hover:-translate-y-0.5 inline-flex items-center gap-2 shadow-[0_8px_30px_rgba(0,186,207,0.12)]"
              >
                <TrendingUp className="h-4.5 w-4.5" />
                <span>Calcular Mi Ganancia</span>
              </a>
            </motion.div>
          </div>

          {/* Hero Right image and badges */}
          <div className="lg:col-span-5 relative flex justify-center items-center">
            <motion.div 
              initial={{ opacity: 0, scale: 0.9, rotate: -3 }}
              animate={{ opacity: 1, scale: 1, rotate: 0 }}
              transition={{ duration: 1, ease: "easeOut" }}
              className="relative w-full max-w-md aspect-[4/5] rounded-[2.5rem] bg-[#F5EFE6] border border-white p-4 shadow-xl overflow-hidden"
            >
              <div className="relative w-full h-full rounded-[2rem] overflow-hidden shadow-sm group">
                <img 
                  src={homepageSettings.featured_image} 
                  alt="Yogurt Líquido Sabor Fresa" 
                  className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105"
                />
                
                {/* Visual overlay card with turquoise details */}
                <div className="absolute bottom-6 left-6 right-6 bg-white/90 backdrop-blur-md border border-white/20 p-5 rounded-2xl flex items-center justify-between shadow-lg">
                  <div>
                    <h4 className="font-jakarta font-extrabold text-slate-900 text-sm">Yogurt Líquido Fresa</h4>
                    <p className="text-[10px] font-bold text-[#FF007F] mt-1 uppercase tracking-widest">Nutre tu día, cuida de ti</p>
                  </div>
                  <div className="flex items-center gap-1 bg-[#00BACF] text-white rounded-full px-3 py-1 font-manrope font-extrabold text-xs">
                    <span>100% Calidad</span>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Absolute Venezuela stamp */}
            <motion.div 
              initial={{ scale: 0, rotate: 15 }}
              animate={{ scale: 1, rotate: -8 }}
              transition={{ delay: 0.7, type: "spring", stiffness: 100 }}
              className="absolute -top-4 -right-4 bg-pink-500 text-white border-2 border-white px-5 py-3 rounded-2xl shadow-xl font-caveat text-xl sm:text-2xl z-20 flex flex-col items-center leading-none"
            >
              <span>¡Hecho en!</span>
              <span className="font-extrabold tracking-widest text-base font-manrope mt-1">VENEZUELA 🇻🇪</span>
            </motion.div>
          </div>
        </div>
      </section>

      {/* --- Highlight Editorial Marquee (Turquoise borders) --- */}
      <section className="bg-pink-600 py-6 border-y-2 border-pink-700 overflow-hidden relative select-none">
        <div className="animate-marquee whitespace-nowrap flex font-jakarta font-extrabold text-white text-2xl sm:text-3xl tracking-tight leading-none uppercase italic">
          <span className="mx-6">🍓 DISTRIBUIDORES MAYORISTAS •</span>
          <span className="mx-6 text-pink-100">PRECIOS DE FÁBRICA •</span>
          <span className="mx-6">YOGURT LÍQUIDO Y FIRME •</span>
          <span className="mx-6 text-pink-100">IDEAL PARA EMPRENDEDORES •</span>
          <span className="mx-6">EXCELENTE MARGEN DE GANANCIA •</span>
          <span className="mx-6 text-pink-100">PEDIDOS EN LÍNEA SEGUROS (STRIPE) •</span>
          <span className="mx-6">🍓 DISTRIBUIDORES MAYORISTAS •</span>
          <span className="mx-6 text-pink-100">PRECIOS DE FÁBRICA •</span>
          <span className="mx-6">YOGURT LÍQUIDO Y FIRME •</span>
          <span className="mx-6 text-pink-100">IDEAL PARA EMPRENDEDORES •</span>
          <span className="mx-6">EXCELENTE MARGEN DE GANANCIA •</span>
          <span className="mx-6 text-pink-100">PEDIDOS EN LÍNEA SEGUROS (STRIPE) •</span>
        </div>
      </section>

      {/* --- Interactive Wholesale Calculator Section (Turquoise accents) --- */}
      <section id="calculadora" className="py-24 bg-[#FAF6EE] relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 relative z-10 text-left">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <span className="text-xs font-extrabold tracking-[0.22em] text-[#00BACF] uppercase">CALCULADORA DE NEGOCIO</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-jakarta font-extrabold text-slate-950 mt-3 leading-tight tracking-tight">
              Calcula Tu Ganancia Como Distribuidor
            </h2>
            <p className="text-sm sm:text-base text-slate-600 mt-4 leading-relaxed font-manrope">
              Ingresa tu presupuesto estimado de compra y visualiza cuánta mercancía puedes obtener y el retorno de inversión vendiendo Nutriyog al detal.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center bg-white border border-[#F5EFE6] p-8 sm:p-12 rounded-[2.5rem] shadow-xl max-w-5xl mx-auto">
            {/* Input Side */}
            <div className="lg:col-span-6 space-y-6">
              <h3 className="text-xl sm:text-2xl font-jakarta font-extrabold text-slate-900 flex items-center gap-3">
                <Store className="h-6 w-6 text-pink-500" />
                <span>Simulador de Compra</span>
              </h3>
              
              <div className="space-y-2">
                <label className="text-xs font-extrabold text-slate-500 uppercase tracking-widest block">Presupuesto de Compra ($ USD)</label>
                <div className="flex items-center gap-4">
                  <input 
                    type="range" 
                    min={50} 
                    max={1000} 
                    step={50}
                    value={calcBudget}
                    onChange={(e) => setCalcBudget(Number(e.target.value))}
                    className="w-full accent-[#00BACF] h-2 bg-slate-100 rounded-lg cursor-pointer"
                  />
                  <span className="text-2xl font-black text-pink-600 font-jakarta min-w-[90px] text-right">
                    {formatPrice(calcBudget)}
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 pt-4 text-xs font-semibold uppercase tracking-wider text-slate-500">
                <div className="p-4 bg-[#FAF6EE] rounded-2xl border border-[#F5EFE6]">
                  <span className="block text-[10px]">Inversión Mínima</span>
                  <span className="text-lg font-black text-slate-800 block mt-1">$50.00</span>
                </div>
                <div className="p-4 bg-[#FAF6EE] rounded-2xl border border-[#F5EFE6]">
                  <span className="block text-[10px]">Margen Promedio</span>
                  <span className="text-lg font-black text-green-600 block mt-1">40% Neto</span>
                </div>
              </div>
            </div>

            {/* Output results side */}
            <div className="lg:col-span-6 p-6 sm:p-8 rounded-[2rem] bg-gradient-to-tr from-cyan-50 to-pink-50/50 border border-white flex flex-col justify-between h-full relative">
              <div className="absolute top-6 right-6 p-2 rounded-full bg-white shadow-sm text-pink-500"><Award className="h-5 w-5" /></div>
              
              <div className="space-y-4">
                <span className="text-[10px] font-extrabold text-slate-400 uppercase tracking-widest block">RESULTADOS ESTIMADOS</span>
                
                <div>
                  <span className="text-xs text-slate-500 font-semibold block">Puedes adquirir aproximadamente:</span>
                  <div className="space-y-2 mt-2 font-manrope text-sm font-bold text-slate-800">
                    <div className="flex justify-between">
                      <span>Combos Emprendedores Completos:</span>
                      <span className="text-[#00BACF] font-black">{calcResults.combos} combos</span>
                    </div>
                    <div className="flex justify-between pb-3 border-b border-slate-200/50">
                      <span>O Yogurt Líquido Individual (200ml):</span>
                      <span className="text-[#00BACF] font-black">~ {calcResults.yogurts} unidades</span>
                    </div>
                  </div>
                </div>

                <div className="pt-2">
                  <span className="text-xs text-slate-500 font-semibold block">Ganancia estimada al revender al detal:</span>
                  <span className="text-3xl sm:text-4xl font-jakarta font-black text-green-600 block mt-1">
                    +{formatPrice(calcResults.profit)}
                  </span>
                  <span className="text-[10px] text-slate-400 font-extrabold block uppercase mt-1 tracking-widest">
                    Margen de retorno calculado al 40% de sobreprecio de detal
                  </span>
                </div>
              </div>

              <a 
                href="#productos"
                className="mt-6 w-full py-4 rounded-full bg-slate-950 hover:bg-pink-600 text-white font-manrope font-bold text-xs tracking-wider uppercase text-center transition-colors duration-300 block"
              >
                Comprar Mercancía Ahora
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* --- Brand Manifesto Chapters --- */}
      <section id="manifiesto" className="py-24 bg-white relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 relative z-10 text-left">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <span className="text-xs font-extrabold tracking-[0.2em] text-[#00BACF] uppercase">NUESTRO VALOR</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-jakarta font-extrabold text-slate-950 mt-3 leading-tight tracking-tight">
              ¿Por qué vender Nutriyog en tu negocio?
            </h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mt-12">
            {/* Chapter 01 */}
            <motion.div 
              variants={revealVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              className="bg-[#FAF6EE] p-8 rounded-[2rem] border border-[#F5EFE6] shadow-sm relative group"
            >
              <div className="absolute top-6 right-8 text-5xl font-jakarta font-black text-[#E8DFC9] group-hover:text-pink-100 transition-colors duration-300">01</div>
              <div className="p-3 bg-pink-50 text-pink-500 rounded-2xl w-fit mb-6">
                <TrendingUp className="h-6 w-6" />
              </div>
              <h3 className="text-lg font-jakarta font-extrabold text-slate-950 mb-3">Rotación Veloz</h3>
              <p className="text-slate-600 text-sm leading-relaxed font-manrope">
                El yogurt de fresa y firme tiene una demanda masiva diaria. Su exquisito sabor natural asegura que los clientes repitan la compra de forma constante en bodegas, colegios y panaderías.
              </p>
            </motion.div>

            {/* Chapter 02 */}
            <motion.div 
              variants={revealVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              className="bg-[#FAF6EE] p-8 rounded-[2rem] border border-[#F5EFE6] shadow-sm relative group"
            >
              <div className="absolute top-6 right-8 text-5xl font-jakarta font-black text-[#E8DFC9] group-hover:text-cyan-100 transition-colors duration-300">02</div>
              <div className="p-3 bg-cyan-50 text-[#00BACF] rounded-2xl w-fit mb-6">
                <Store className="h-6 w-6" />
              </div>
              <h3 className="text-lg font-jakarta font-extrabold text-slate-950 mb-3">Precios Sin Competencia</h3>
              <p className="text-slate-600 text-sm leading-relaxed font-manrope">
                Al comprar directo de fábrica, eliminas los intermediarios y logras el mejor precio del mercado para revender, compitiendo fácilmente con otras marcas de menor calidad.
              </p>
            </motion.div>

            {/* Chapter 03 */}
            <motion.div 
              variants={revealVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              className="bg-[#FAF6EE] p-8 rounded-[2rem] border border-[#F5EFE6] shadow-sm relative group"
            >
              <div className="absolute top-6 right-8 text-5xl font-jakarta font-black text-[#E8DFC9] group-hover:text-pink-100 transition-colors duration-300">03</div>
              <div className="p-3 bg-pink-50 text-pink-500 rounded-2xl w-fit mb-6">
                <ShieldCheck className="h-6 w-6" />
              </div>
              <h3 className="text-lg font-jakarta font-extrabold text-slate-950 mb-3">Soporte al Distribuidor</h3>
              <p className="text-slate-600 text-sm leading-relaxed font-manrope">
                Ofrecemos material publicitario digital, despachos directos coordinados y atención continua para que tu negocio crezca junto con nuestra marca.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* --- Bento Grid Product Catalog with Quantity Selector (Turquoise style) --- */}
      <section id="productos" className="py-24 bg-white relative">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <span className="text-xs font-extrabold tracking-[0.2em] text-pink-500 uppercase">TIENDA AL MAYOR</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-jakarta font-extrabold text-slate-950 mt-3 leading-tight tracking-tight">
              Precios Mayoristas & Promos Emprendedor
            </h2>
            <p className="text-sm sm:text-base text-slate-600 mt-4 font-manrope">
              Suma las cantidades de combos o productos individuales que desees. Haz tu pedido al mayor y paga seguro vía Stripe.
            </p>
          </div>

          {/* Catalog bento grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {products.map((product) => {
              const isCombo = product.id === "combo_emprendedor";
              const selectedQty = selectedQuantities[product.id] || product.min_qty;

              return (
                <motion.div 
                  key={product.id}
                  variants={revealVariants}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, margin: "-50px" }}
                  className={`bg-white border border-[#F5EFE6] rounded-[2rem] overflow-hidden flex flex-col hover:border-pink-200 hover:shadow-xl transition-all duration-500 shadow-sm ${
                    isCombo ? "md:col-span-2 lg:col-span-2 bg-gradient-to-br from-pink-50/10 via-white to-cyan-50/5" : ""
                  }`}
                >
                  {/* Badge & Sizing */}
                  <div className="relative aspect-video w-full overflow-hidden group">
                    <img 
                      src={product.image} 
                      alt={product.name} 
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                    <div className="absolute top-4 left-4 flex gap-2">
                      <span className="bg-pink-500 text-white font-manrope font-extrabold text-[10px] uppercase tracking-wider px-4 py-1.5 rounded-full shadow-md">
                        {product.badge}
                      </span>
                      <span className="bg-slate-900/80 backdrop-blur-md text-white font-manrope font-bold text-[10px] px-3 py-1.5 rounded-full uppercase tracking-wider">
                        {product.size}
                      </span>
                    </div>
                  </div>

                  {/* Body details */}
                  <div className="p-8 flex flex-col flex-grow text-left">
                    <div className="flex justify-between items-start gap-4">
                      <h3 className="text-xl sm:text-2xl font-jakarta font-extrabold text-slate-950 leading-tight">
                        {product.name}
                      </h3>
                      <div className="text-right flex-shrink-0">
                        <span className="text-2xl font-jakarta font-black text-pink-600 block leading-none">
                          {formatPrice(product.price)}
                        </span>
                        <span className="text-[9px] text-[#A69C95] font-extrabold block uppercase tracking-widest mt-1">AL MAYOR</span>
                      </div>
                    </div>

                    <p className="text-sm text-slate-600 mt-4 leading-relaxed flex-grow font-manrope">
                      {product.description}
                    </p>

                    {/* Minimum Limit Badge */}
                    {product.min_qty > 1 && (
                      <div className="mt-4 flex items-center gap-2 text-cyan-700 bg-cyan-50 px-4 py-2.5 rounded-2xl text-[11px] font-semibold border border-cyan-100">
                        <AlertCircle className="h-4 w-4 flex-shrink-0 text-[#00BACF]" />
                        <span>Cantidad mínima de compra: {product.min_qty} unidades para esta presentación.</span>
                      </div>
                    )}

                    {/* Interactive Quantity Selector directly on Card */}
                    <div className="mt-6 pt-6 border-t border-[#F5EFE6] flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                      <div className="flex items-center gap-3">
                        <span className="text-xs font-bold text-[#544B45] uppercase tracking-wider">Cantidad:</span>
                        
                        <div className="flex items-center gap-2.5 border border-[#D5CBBF] rounded-full px-2.5 py-1.5 bg-[#FAF6EE]">
                          <button 
                            onClick={() => handleCardQtyChange(product.id, -1, product.min_qty)}
                            data-testid={`card-qty-decrement-${product.id}`}
                            className="p-1 hover:text-pink-500 text-[#544B45] transition-colors"
                          >
                            <Minus className="h-4 w-4" />
                          </button>
                          <span className="text-sm font-black font-jakarta text-slate-800 px-1 min-w-[24px] text-center">
                            {selectedQty}
                          </span>
                          <button 
                            onClick={() => handleCardQtyChange(product.id, 1, product.min_qty)}
                            data-testid={`card-qty-increment-${product.id}`}
                            className="p-1 hover:text-pink-500 text-[#544B45] transition-colors"
                          >
                            <Plus className="h-4 w-4" />
                          </button>
                        </div>
                      </div>

                      {/* Display subtotal of selection */}
                      <div className="text-right">
                        <span className="text-[10px] text-[#A69C95] uppercase tracking-wider block font-bold">Subtotal:</span>
                        <span className="text-base font-jakarta font-black text-slate-900 block">
                          {formatPrice(product.price * selectedQty)}
                        </span>
                      </div>
                    </div>

                    {/* Buy CTA with turquoise color */}
                    <button 
                      onClick={() => handleAddToCart(product)}
                      data-testid={`add-to-cart-button-${product.id}`}
                      className="mt-6 w-full py-4.5 rounded-full bg-[#00BACF] hover:bg-cyan-600 text-white font-manrope font-bold text-xs tracking-widest uppercase transition-all duration-300 flex items-center justify-center gap-3 hover:-translate-y-0.5 active:translate-y-0 shadow-[0_8px_30px_rgba(0,186,207,0.15)]"
                    >
                      <ShoppingBag className="h-4.5 w-4.5" />
                      <span>Agregar {selectedQty} al Carrito</span>
                    </button>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* --- Wholesaler Testimonials --- */}
      <section className="py-24 bg-pink-50/10 relative text-left">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <span className="text-xs font-extrabold tracking-widest text-[#00BACF] uppercase">ÉXITO COMERCIAL</span>
            <h2 className="text-3xl sm:text-4xl font-jakarta font-extrabold text-slate-950 mt-3 leading-tight tracking-tight">
              Testimonios de Emprendedores y Comercios
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-3xl border border-pink-100/50 shadow-sm">
              <div className="flex gap-1 text-pink-500 mb-4">
                {[...Array(5)].map((_, i) => <Star key={i} className="h-4 w-4 fill-current" />)}
              </div>
              <p className="text-sm text-slate-600 italic leading-relaxed font-manrope">
                {"\"El Yogurt Líquido de 200ml al mayor es la estrella en mi panadería. Compro 4 cajas semanales y se agotan en días. ¡Los márgenes de ganancia son excelentes!\""}
              </p>
              <div className="mt-6 flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-pink-100 flex items-center justify-center text-pink-600 font-jakarta font-bold text-xs">MV</div>
                <div>
                  <h4 className="font-jakarta font-bold text-slate-900 text-sm">María Valentina</h4>
                  <p className="text-xs text-slate-400">Panadería Las Rosas • Caracas</p>
                </div>
              </div>
            </div>

            <div className="bg-white p-8 rounded-3xl border border-pink-100/50 shadow-sm">
              <div className="flex gap-1 text-pink-500 mb-4">
                {[...Array(5)].map((_, i) => <Star key={i} className="h-4 w-4 fill-current" />)}
              </div>
              <p className="text-sm text-slate-600 italic leading-relaxed font-manrope">
                {"\"Comencé mi emprendimiento de distribución con el Combo Emprendedor de $50. Los yogures firmes de 500g se venden súper rápido en mi comunidad escolar. Muy recomendado para iniciar.\""}
              </p>
              <div className="mt-6 flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-cyan-100 flex items-center justify-center text-cyan-600 font-jakarta font-bold text-xs">AG</div>
                <div>
                  <h4 className="font-jakarta font-bold text-slate-900 text-sm">Andrés González</h4>
                  <p className="text-xs text-slate-400">Emprendedor Independiente • Maracay</p>
                </div>
              </div>
            </div>

            <div className="bg-white p-8 rounded-3xl border border-pink-100/50 shadow-sm">
              <div className="flex gap-1 text-pink-500 mb-4">
                {[...Array(5)].map((_, i) => <Star key={i} className="h-4 w-4 fill-current" />)}
              </div>
              <p className="text-sm text-slate-600 italic leading-relaxed font-manrope">
                {"\"La cremosidad del yogurt firme natural no tiene competencia en Valencia. Mis clientes en el bodegón lo buscan para sus postres y desayunos. Compra en línea segura.\""}
              </p>
              <div className="mt-6 flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-pink-100 flex items-center justify-center text-pink-600 font-jakarta font-bold text-xs">CL</div>
                <div>
                  <h4 className="font-jakarta font-bold text-slate-900 text-sm">Clara Lugo</h4>
                  <p className="text-xs text-slate-400">Bodegón Sabor Real • Valencia</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* --- Footer & Contact info --- */}
      <footer id="contacto" className="bg-[#1A1614] text-[#A69C95] py-16 border-t border-[#29221F] text-left">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-5 flex flex-col items-start">
            <img src={LOGO_IMAGE_URL} alt="Nutriyog Logo" className="h-16 w-auto mb-6 rounded-full border border-white/10" />
            <p className="text-sm leading-relaxed max-w-sm">
              Llevando la mejor calidad y rentabilidad mayorista de yogurt artesanal a cada rincón de Venezuela. Ideal para bodegas, panaderías, colegios y distribución.
            </p>
            <div className="mt-6 space-y-3 font-manrope font-bold text-sm text-white">
              <div className="flex items-center gap-4">
                <Phone className="h-5 w-5 text-pink-500" />
                <span>+58 422 809 8318</span>
              </div>
              <div className="flex items-center gap-4">
                <Mail className="h-5 w-5 text-pink-500" />
                <a href="mailto:info@nutriyog.com.ve" className="hover:text-pink-500 transition-colors">info@nutriyog.com.ve</a>
              </div>
              <div className="flex items-start gap-4">
                <MapPin className="h-5 w-5 text-pink-500 mt-0.5 flex-shrink-0" />
                <span className="text-[#A69C95] font-semibold text-xs leading-relaxed">
                  Estamos ubicados en: <span className="text-white font-bold">San Antonio de los Altos, Recta de las Minas, estado Miranda</span>
                </span>
              </div>
            </div>
          </div>

          <div className="md:col-span-3">
            <h4 className="text-white font-manrope font-bold text-[10px] uppercase tracking-[0.25em] mb-6">Navegación</h4>
            <div className="flex flex-col gap-4 text-xs uppercase tracking-wider font-semibold">
              <a href="#" className="hover:text-pink-500 transition-colors">Inicio</a>
              <a href="#productos" className="hover:text-pink-500 transition-colors">Precios al Mayor</a>
              <a href="#calculadora" className="hover:text-pink-500 transition-colors">Calculadora</a>
              <a href="#manifiesto" className="hover:text-pink-500 transition-colors">Historia</a>
            </div>
          </div>

          <div className="md:col-span-4 text-left">
            <h4 className="text-white font-manrope font-bold text-[10px] uppercase tracking-[0.25em] mb-6">Atención al Distribuidor</h4>
            <p className="text-sm leading-relaxed mb-4">
              ¿Deseas distribuir Nutriyog en tu estado? Escríbenos directamente o visítanos para coordinar despachos especiales.
            </p>
            <span className="text-xs text-pink-500 font-extrabold uppercase tracking-widest block">Hecho con Amor en Venezuela 🇻🇪</span>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-6 mt-16 pt-8 border-t border-[#29221F] text-center text-xs flex flex-col sm:flex-row items-center justify-between gap-4">
          <span>&copy; 2025 Nutriyog. Todos los derechos reservados.</span>
        </div>
      </footer>

      {/* --- Floating WhatsApp Bubble --- */}
      <a 
        href="https://wa.me/584228098318?text=%C2%A1Hola%20Nutriyog%21%20Me%20gustar%C3%ADa%20hacer%20una%20consulta%20sobre%20pedidos%20al%20mayor%20para%20mi%20negocio."
        target="_blank"
        rel="noreferrer"
        data-testid="whatsapp-bubble-link"
        className="fixed bottom-6 right-6 z-40 p-4 bg-green-500 hover:bg-green-600 text-white rounded-full shadow-2xl hover:scale-110 active:scale-95 transition-all duration-300 animate-pulse-soft flex items-center justify-center border-2 border-white"
      >
        <MessageCircle className="h-6 w-6" />
      </a>

      {/* --- Shopping Cart Drawer --- */}
      <AnimatePresence>
        {isCartOpen && (
          <>
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.5 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsCartOpen(false)}
              className="fixed inset-0 bg-black z-50 cursor-pointer"
            />

            <motion.div 
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "tween", duration: 0.3 }}
              className="fixed right-0 top-0 bottom-0 w-full max-w-md bg-white z-50 shadow-2xl flex flex-col text-left"
            >
              {/* Header */}
              <div className="p-6 border-b border-[#F5EFE6] flex items-center justify-between bg-[#FAF6EE]/50">
                <div className="flex items-center gap-3">
                  <ShoppingBag className="h-6 w-6 text-[#00BACF]" />
                  <h3 className="font-jakarta font-extrabold text-xl text-slate-950">Carrito Mayorista</h3>
                </div>
                <button 
                  onClick={() => setIsCartOpen(false)}
                  data-testid="cart-close-button"
                  className="p-2 rounded-full hover:bg-slate-100 transition-colors text-slate-600"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {/* Unified Scrollable Container containing everything */}
              <div className="flex-grow overflow-y-auto no-scrollbar flex flex-col justify-between">
                
                {/* Items List container */}
                <div className="p-6">
                {cart.length === 0 ? (
                  <div className="h-full flex flex-col items-center justify-center text-center py-20">
                    <ShoppingBag className="h-16 w-16 text-pink-200 stroke-[1.2] mb-4" />
                    <h4 className="font-jakarta font-bold text-slate-900 text-base">Tu carrito está vacío</h4>
                    <p className="text-xs text-slate-400 mt-2 max-w-[240px]">Agrega productos del catálogo mayorista para comenzar.</p>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {cart.map((item) => (
                      <div key={item.id} className="flex gap-4 border-b border-slate-100 pb-6 items-start">
                        <img src={item.image} alt={item.name} className="w-16 h-16 object-cover rounded-2xl border border-slate-100 flex-shrink-0" />
                        <div className="flex-grow">
                          <h4 className="font-jakarta font-bold text-[#2E2825] text-sm leading-tight">{item.name}</h4>
                          <span className="text-[10px] text-pink-500 font-extrabold mt-1 block uppercase tracking-wide">{item.badge}</span>
                          
                          <div className="flex items-center justify-between mt-3">
                            <span className="font-jakarta font-black text-sm text-slate-900">{formatPrice(item.price * item.quantity)}</span>
                            
                            {/* Quantity buttons inside cart */}
                            <div className="flex items-center gap-2 border border-[#D5CBBF] rounded-full px-2 py-1 bg-[#FAF6EE]">
                              <button 
                                onClick={() => handleUpdateQuantityInCart(item.id, item.quantity - 1, item.min_qty)}
                                data-testid={`qty-decrement-${item.id}`}
                                className="p-1 hover:text-pink-500 text-slate-500 transition-colors"
                              >
                                <Minus className="h-3 w-3" />
                              </button>
                              <span className="text-xs font-bold font-manrope text-slate-800 px-1 min-w-[18px] text-center">{item.quantity}</span>
                              <button 
                                onClick={() => handleUpdateQuantityInCart(item.id, item.quantity + 1, item.min_qty)}
                                data-testid={`qty-increment-${item.id}`}
                                className="p-1 hover:text-pink-500 text-slate-500 transition-colors"
                              >
                                <Plus className="h-3 w-3" />
                              </button>
                            </div>

                            <button 
                              onClick={() => handleRemoveFromCart(item.id)}
                              data-testid={`cart-remove-item-${item.id}`}
                              className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-full transition-all"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                </div> {/* Close Items List container */}

                {/* Form & Footer container inside the scrollable view */}
                {cart.length > 0 && (
                  <div className="border-t border-[#F5EFE6] p-6 bg-[#FAF6EE]/30">
                  <form onSubmit={handleCheckout} className="space-y-4 mb-6">
                    <h4 className="font-manrope font-extrabold text-[10px] uppercase tracking-wider text-slate-900">Detalles de Entrega</h4>
                    
                    <div>
                      <label className="text-[9px] font-extrabold text-slate-600 uppercase tracking-widest block mb-1">Nombre Completo *</label>
                      <input 
                        type="text" 
                        required
                        value={checkoutForm.name}
                        onChange={(e) => setCheckoutForm({ ...checkoutForm, name: e.target.value })}
                        data-testid="checkout-name-input"
                        placeholder="Ej. Juan Pérez"
                        className="w-full px-4 py-2.5 rounded-xl border border-[#D5CBBF] text-sm focus:outline-none focus:ring-2 focus:ring-pink-500 bg-white"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-[9px] font-extrabold text-slate-600 uppercase tracking-widest block mb-1">Email *</label>
                        <input 
                          type="email" 
                          required
                          value={checkoutForm.email}
                          onChange={(e) => setCheckoutForm({ ...checkoutForm, email: e.target.value })}
                          data-testid="checkout-email-input"
                          placeholder="juan@email.com"
                          className="w-full px-4 py-2.5 rounded-xl border border-[#D5CBBF] text-sm focus:outline-none focus:ring-2 focus:ring-pink-500 bg-white"
                        />
                      </div>
                      <div>
                        <label className="text-[9px] font-extrabold text-slate-600 uppercase tracking-widest block mb-1">Celular / Teléfono *</label>
                        <input 
                          type="tel" 
                          required
                          value={checkoutForm.phone}
                          onChange={(e) => setCheckoutForm({ ...checkoutForm, phone: e.target.value })}
                          data-testid="checkout-phone-input"
                          placeholder="Ej. +584121234567"
                          className="w-full px-4 py-2.5 rounded-xl border border-[#D5CBBF] text-sm focus:outline-none focus:ring-2 focus:ring-pink-500 bg-white"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-2 p-1 bg-[#F5EFE6] rounded-xl">
                      <button 
                        type="button"
                        onClick={() => setCheckoutForm({ ...checkoutForm, deliveryType: "pickup" })}
                        className={`py-2 text-[10px] font-extrabold uppercase rounded-lg transition-all ${
                          checkoutForm.deliveryType === "pickup" ? "bg-white text-pink-600 shadow-sm" : "text-[#544B45]"
                        }`}
                      >
                        Retiro en Tienda
                      </button>
                      <button 
                        type="button"
                        onClick={() => setCheckoutForm({ ...checkoutForm, deliveryType: "delivery" })}
                        className={`py-2 text-[10px] font-extrabold uppercase rounded-lg transition-all ${
                          checkoutForm.deliveryType === "delivery" ? "bg-white text-pink-600 shadow-sm" : "text-[#544B45]"
                        }`}
                      >
                        Despacho / Delivery
                      </button>
                    </div>

                    {checkoutForm.deliveryType === "delivery" && (
                      <div>
                        <label className="text-[9px] font-extrabold text-slate-600 uppercase tracking-widest block mb-1">Dirección de Despacho *</label>
                        <textarea 
                          required
                          value={checkoutForm.deliveryAddress}
                          onChange={(e) => setCheckoutForm({ ...checkoutForm, deliveryAddress: e.target.value })}
                          data-testid="checkout-address-input"
                          placeholder="Calle, Edificio, Número de Apto..."
                          rows={2}
                          className="w-full px-4 py-2.5 rounded-xl border border-[#D5CBBF] text-sm focus:outline-none focus:ring-2 focus:ring-pink-500 bg-white"
                        />
                      </div>
                    )}

                    {/* Payment Method Selector */}
                    <div className="space-y-3 pt-3 border-t border-[#F5EFE6]">
                      <h5 className="font-manrope font-extrabold text-[10px] uppercase tracking-wider text-slate-900">Método de Pago</h5>
                      
                      <div className="grid grid-cols-1 gap-2.5">
                        {paymentMethods.stripe?.enabled && (
                          <label className={`flex items-center gap-3 p-3.5 rounded-xl border cursor-pointer transition-all ${
                            selectedPaymentMethod === "stripe" ? "border-[#00BACF] bg-[#00BACF]/5" : "border-[#D5CBBF] bg-white hover:border-slate-400"
                          }`}>
                            <input 
                              type="radio" 
                              name="payment_method" 
                              value="stripe" 
                              checked={selectedPaymentMethod === "stripe"}
                              onChange={() => setSelectedPaymentMethod("stripe")}
                              className="accent-[#00BACF]"
                            />
                            <div>
                              <span className="font-jakarta font-extrabold text-xs block text-slate-900">Tarjeta de Crédito Internacional (Stripe)</span>
                              <span className="text-[10px] text-slate-400 block mt-0.5">Pago seguro en línea procesado al instante</span>
                            </div>
                          </label>
                        )}

                        {paymentMethods.pago_movil?.enabled && (
                          <label className={`flex items-center gap-3 p-3.5 rounded-xl border cursor-pointer transition-all ${
                            selectedPaymentMethod === "pago_movil" ? "border-[#00BACF] bg-[#00BACF]/5" : "border-[#D5CBBF] bg-white hover:border-slate-400"
                          }`}>
                            <input 
                              type="radio" 
                              name="payment_method" 
                              value="pago_movil" 
                              checked={selectedPaymentMethod === "pago_movil"}
                              onChange={() => setSelectedPaymentMethod("pago_movil")}
                              className="accent-[#00BACF]"
                            />
                            <div>
                              <span className="font-jakarta font-extrabold text-xs block text-slate-900">Pago Móvil (Bolívares Bs. BCV)</span>
                              <span className="text-[10px] text-slate-400 block mt-0.5">Realiza el pago móvil y carga tu referencia</span>
                            </div>
                          </label>
                        )}
                      </div>
                    </div>

                    {/* Pago Móvil Details Inputs */}
                    {selectedPaymentMethod === "pago_movil" && (
                      <div className="p-4 bg-[#FAF6EE] rounded-2xl border border-[#F5EFE6] space-y-3 mt-3">
                        <span className="text-[9px] text-[#A69C95] font-black uppercase tracking-widest block text-left">DATOS DE TRANSFERENCIA PAGO MÓVIL</span>
                        <div className="bg-white p-3.5 rounded-2xl border border-slate-100 space-y-3">
                          <span className="text-[9px] text-[#A69C95] font-black uppercase tracking-widest block text-left mb-1">📢 TOCA CUALQUIER DATO PARA COPIARLO:</span>
                          {paymentMethods.pago_movil?.details ? (
                            <div className="flex flex-col gap-3">
                              {paymentMethods.pago_movil.details.split("•").map((part, i) => {
                                const trimmed = part.trim();
                                if (!trimmed) return null;
                                const splitIndex = trimmed.indexOf(":");
                                let label = "Dato de Pago";
                                let value = trimmed;
                                if (splitIndex > -1) {
                                  label = trimmed.substring(0, splitIndex).trim();
                                  value = trimmed.substring(splitIndex + 1).trim();
                                } else {
                                  const lower = trimmed.toLowerCase();
                                  if (lower.includes("banco") || lower.includes("banesco") || lower.includes("mercantil") || lower.includes("provincial")) {
                                    label = "Banco";
                                  } else if (lower.includes("teléfono") || lower.includes("telefono") || lower.includes("celular")) {
                                    label = "Teléfono";
                                  } else if (lower.includes("c.i.") || lower.includes("rif") || lower.includes("cédula") || lower.includes("cedula")) {
                                    label = "C.I. / RIF / Cédula";
                                  }
                                }
                                return (
                                  <div key={i} className="flex flex-col gap-1 text-left">
                                    <span className="text-[9px] text-[#00BACF] font-black uppercase tracking-wider">{label}</span>
                                    <CopyableText text={value} label={label} />
                                  </div>
                                );
                              })}
                            </div>
                          ) : (
                            <p className="text-xs text-slate-500 italic">No hay instrucciones de pago registradas.</p>
                          )}
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="text-[9px] font-extrabold text-slate-500 uppercase tracking-widest block mb-1 text-left">Monto Pagado *</label>
                            <input 
                              type="text"
                              required
                              placeholder="Ej. Bs. 1.200 o $1.62"
                              value={pagoMovilDetails.amount_paid}
                              onChange={(e) => setPagoMovilDetails({ ...pagoMovilDetails, amount_paid: e.target.value })}
                              className="w-full px-3 py-2 border border-[#D5CBBF] rounded-xl bg-white text-xs font-bold focus:outline-none"
                            />
                          </div>
                          <div>
                            <label className="text-[9px] font-extrabold text-slate-500 uppercase tracking-widest block mb-1 text-left">Referencia *</label>
                            <input 
                              type="text"
                              required
                              placeholder="Últimos 4-6 dígitos"
                              value={pagoMovilDetails.reference}
                              onChange={(e) => setPagoMovilDetails({ ...pagoMovilDetails, reference: e.target.value })}
                              className="w-full px-3 py-2 border border-[#D5CBBF] rounded-xl bg-white text-xs font-bold focus:outline-none"
                            />
                          </div>
                        </div>

                        <div>
                          <label className="text-[9px] font-extrabold text-slate-500 uppercase tracking-widest block mb-1 text-left">Teléfono o Banco emisor *</label>
                          <input 
                            type="text"
                            required
                            placeholder="Ej. 0412-1234567 (Banesco)"
                            value={pagoMovilDetails.bank_phone}
                            onChange={(e) => setPagoMovilDetails({ ...pagoMovilDetails, bank_phone: e.target.value })}
                            className="w-full px-3 py-2 border border-[#D5CBBF] rounded-xl bg-white text-xs font-bold focus:outline-none"
                          />
                        </div>
                      </div>
                    )}
                  </form>

                  {/* Pricing Summary */}
                  <div className="space-y-2 border-t border-[#F5EFE6] pt-4 mb-6 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-500">Subtotal:</span>
                      <span className="font-jakarta font-bold text-slate-900">{formatPrice(getCartSubtotal())}</span>
                    </div>
                    {checkoutForm.deliveryType === "delivery" && (
                      <div className="flex justify-between">
                        <span className="text-slate-500">Envío / Delivery:</span>
                        <span className="font-jakarta font-bold text-slate-900">
                          {getShippingFee(getCartSubtotal()) === 0 ? (
                            <span className="text-green-600 font-extrabold uppercase text-xs tracking-wider">¡GRATIS!</span>
                          ) : (
                            formatPrice(getShippingFee(getCartSubtotal()))
                          )}
                        </span>
                      </div>
                    )}
                    <div className="flex justify-between border-t border-[#F5EFE6] pt-3 text-base">
                      <span className="font-jakarta font-extrabold text-slate-950">Total:</span>
                      <span className="font-jakarta font-black text-lg text-[#00BACF]">
                        {formatPrice(getCartSubtotal() + getShippingFee(getCartSubtotal()))}
                      </span>
                    </div>
                  </div>

                  <button 
                    onClick={handleCheckout}
                    disabled={loading}
                    data-testid="checkout-stripe-button"
                    className="w-full py-4 rounded-full bg-pink-500 hover:bg-pink-600 disabled:bg-slate-400 text-white font-manrope font-extrabold text-xs tracking-widest uppercase transition-all duration-300 shadow-[0_8px_30px_rgba(255,0,127,0.12)] flex items-center justify-center gap-3"
                  >
                    {loading ? (
                      <span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                    ) : (
                      <>
                        <ShieldCheck className="h-4.5 w-4.5" />
                        <span>Pagar con Tarjeta (Stripe)</span>
                      </>
                    )}
                  </button>
                </div>
              )}
              </div> {/* Close Unified Scrollable Container */}
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* --- User Profile / History View (Firestore Based) --- */}
      <AnimatePresence>
        {isProfileOpen && user && (
          <>
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.5 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsProfileOpen(false)}
              className="fixed inset-0 bg-black z-50 cursor-pointer"
            />

            <motion.div 
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "tween", duration: 0.3 }}
              className="fixed right-0 top-0 bottom-0 w-full max-w-xl bg-white z-50 shadow-2xl flex flex-col text-left"
            >
              {/* Profile Header */}
              <div className="p-6 border-b border-[#F5EFE6] flex items-center justify-between bg-[#FAF6EE]">
                <div className="flex items-center gap-3">
                  <User className="h-6 w-6 text-[#00BACF]" />
                  <h3 className="font-jakarta font-extrabold text-xl text-slate-950">Mi Cuenta Distribuidor</h3>
                </div>
                <button 
                  onClick={() => setIsProfileOpen(false)}
                  data-testid="profile-close-button"
                  className="p-2 rounded-full hover:bg-slate-100 transition-colors text-slate-600"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {/* Profile details & orders */}
              <div className="flex-grow overflow-y-auto p-6 no-scrollbar">
                {/* User Card */}
                <div className="bg-[#FAF6EE] p-6 rounded-3xl border border-[#F5EFE6] mb-8">
                  <span className="text-[10px] font-extrabold text-[#A69C95] uppercase tracking-widest block">DATOS DE SESIÓN</span>
                  <h4 className="font-jakarta font-extrabold text-xl text-slate-900 mt-2">{user.first_name} {user.last_name}</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4 text-xs font-semibold text-[#544B45]">
                    <div className="flex items-center gap-2">
                      <Mail className="h-4 w-4 text-pink-500" />
                      <span>{user.email}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Phone className="h-4 w-4 text-pink-500" />
                      <span>{user.phone}</span>
                    </div>
                  </div>
                </div>

                {/* Admin/User Orders Section */}
                {(user.email === "admin@nutriyog.com.ve" || user.email === "DONATEX") ? (
                  <div>
                    {/* Admin Tabs */}
                    <div className="grid grid-cols-2 gap-2 p-1.5 bg-slate-100 rounded-2xl mb-6">
                      <button 
                        onClick={() => setAdminTab("orders")}
                        data-testid="admin-tab-orders"
                        className={`py-3 text-xs font-extrabold uppercase rounded-xl transition-all flex items-center justify-center gap-2 ${
                          adminTab === "orders" ? "bg-[#00BACF] text-white shadow-md" : "text-slate-600 hover:text-slate-900"
                        }`}
                      >
                        <ClipboardList className="h-4 w-4" />
                        <span>Ver Pedidos ({allStoreOrders.length})</span>
                      </button>
                      <button 
                        onClick={() => setAdminTab("catalog")}
                        data-testid="admin-tab-catalog"
                        className={`py-3 text-xs font-extrabold uppercase rounded-xl transition-all flex items-center justify-center gap-2 ${
                          adminTab === "catalog" ? "bg-[#00BACF] text-white shadow-md" : "text-slate-600 hover:text-slate-900"
                        }`}
                      >
                        <Sparkles className="h-4 w-4" />
                        <span>Editar Catálogo</span>
                      </button>
                    </div>

                    {adminTab === "orders" ? (
                      <div>
                        <div className="flex items-center gap-2 mb-4">
                          <ShieldCheck className="h-5 w-5 text-[#00BACF]" />
                          <h4 className="font-jakarta font-extrabold text-base text-[#2E2825]">Todos los Pedidos Recibidos</h4>
                        </div>

                        {allStoreOrders.length === 0 ? (
                          <div className="py-12 text-center bg-slate-50 rounded-2xl border border-dashed border-slate-200">
                            <ClipboardList className="h-10 w-10 text-slate-300 mx-auto stroke-[1.2]" />
                            <p className="text-xs text-slate-400 mt-2">No se han registrado pedidos en la tienda aún.</p>
                          </div>
                        ) : (
                          <div className="space-y-4">
                            {allStoreOrders.map((order, idx) => (
                              <div key={idx} className="border border-cyan-100 p-5 rounded-2xl bg-cyan-50/20 shadow-sm hover:shadow-md transition-all text-left">
                                <div className="flex justify-between items-start border-b border-slate-100 pb-3 mb-3 text-xs">
                                  <div>
                                    <span className="text-[#A69C95] font-bold block uppercase tracking-wider text-[9px]">COMPRADOR / CONTACTO:</span>
                                    <span className="font-manrope font-black text-slate-900 block mt-0.5">{order.client_name}</span>
                                    <span className="font-manrope text-slate-500 block text-[10px] mt-0.5">{order.client_email} • {order.client_phone}</span>
                                  </div>
                                  <div className="text-right">
                                    <span className="text-[#A69C95] font-bold block uppercase tracking-wider text-[9px]">FECHA:</span>
                                    <span className="font-manrope text-slate-600 block mt-0.5">{new Date(order.created_at).toLocaleDateString()}</span>
                                  </div>
                                </div>

                                {/* Items Purchased list */}
                                <div className="space-y-1.5 mb-3 text-xs bg-white p-3.5 rounded-xl border border-slate-100 shadow-inner">
                                  <span className="text-[#A69C95] font-extrabold block uppercase tracking-widest text-[9px] mb-2">PRODUCTOS COMPRADOS:</span>
                                  {order.items.map((it, i) => (
                                    <div key={i} className="flex justify-between text-slate-700">
                                      <span>{it.quantity}x {it.name}</span>
                                      <span className="font-bold">{formatPrice(it.price * it.quantity)}</span>
                                    </div>
                                  ))}
                                </div>

                                {/* Delivery detail */}
                                <div className="text-xs text-slate-600 bg-amber-50/50 p-3 rounded-xl border border-amber-100/50 mb-3">
                                  <span className="text-amber-800 font-bold block text-[9px] uppercase tracking-wider">MÉTODO DE ENTREGA:</span>
                                  <p className="mt-1 font-semibold text-slate-700 leading-relaxed">
                                    {order.delivery_type === "delivery" ? `🚚 Despacho a domicilio: ${order.delivery_address}` : "🏬 Retiro directo en tienda"}
                                  </p>
                                </div>

                                <div className="flex justify-between items-center pt-3 border-t border-slate-100 text-xs">
                                  <div className="flex items-center gap-2">
                                    <span className={`px-2.5 py-1 rounded-full font-manrope font-extrabold text-[9px] uppercase tracking-wider ${
                                      order.payment_status === "paid" ? "bg-green-100 text-green-700" : "bg-yellow-100 text-yellow-700"
                                    }`}>
                                      {order.payment_status === "paid" ? "💰 Pagado" : "⏳ Pendiente"}
                                    </span>
                                  </div>
                                  <span className="font-jakarta font-black text-sm text-pink-600">
                                    Total: {formatPrice(order.total_amount)}
                                  </span>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="space-y-6">
                        <div className="flex items-center justify-between border-b border-slate-100 pb-3 mb-4">
                          <h4 className="font-jakarta font-extrabold text-[#2E2825] flex items-center gap-2 text-sm sm:text-base">
                            <Sparkles className="h-5 w-5 text-pink-500 animate-pulse" />
                            <span>Gestor de Catálogo de Productos</span>
                          </h4>
                          <span className="text-[10px] text-[#00BACF] font-black uppercase tracking-wider">Sincronizado con Firebase</span>
                        </div>

                        {/* Add Product/Campaña Button */}
                        <div className="text-right">
                          <button
                            onClick={() => setShowAddProductForm(!showAddProductForm)}
                            className="px-5 py-2.5 rounded-xl bg-[#00BACF] hover:bg-cyan-600 text-white font-manrope font-extrabold text-xs uppercase tracking-wider transition-all shadow-sm"
                          >
                            {showAddProductForm ? "✕ Cerrar Formulario" : "＋ Crear Nuevo Producto o Combo (Campaña)"}
                          </button>
                        </div>

                        {showAddProductForm && (
                          <form onSubmit={handleCreateProductCMS} className="border-2 border-dashed border-[#00BACF]/50 rounded-3xl p-6 sm:p-8 bg-white text-xs text-left space-y-5 relative overflow-hidden shadow-md">
                            <div className="absolute top-0 left-0 w-2.5 h-full bg-[#00BACF]" />
                            <h5 className="font-jakarta font-black text-slate-800 text-base border-b pb-3 flex items-center gap-2">
                              <Sparkles className="h-5 w-5 text-pink-500 animate-pulse" />
                              <span>Crear Nueva Campaña / Combo de Productos</span>
                            </h5>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                              <div>
                                <label className="text-[9px] font-extrabold text-slate-500 uppercase tracking-widest block mb-1.5">ID Único (Ej: combo_fresa_mini) *</label>
                                <input 
                                  type="text"
                                  required
                                  placeholder="Ej: combo_fresa_mini"
                                  value={newProductForm.id}
                                  onChange={(e) => setNewProductForm({ ...newProductForm, id: e.target.value })}
                                  className="w-full px-4 py-2.5 border border-[#D5CBBF] rounded-xl bg-[#FAF6EE]/20 text-xs font-mono font-bold focus:outline-none"
                                />
                              </div>
                              <div>
                                <label className="text-[9px] font-extrabold text-slate-500 uppercase tracking-widest block mb-1.5">Nombre en Tienda *</label>
                                <input 
                                  type="text"
                                  required
                                  placeholder="Ej. Combo Fresa Mini"
                                  value={newProductForm.name}
                                  onChange={(e) => setNewProductForm({ ...newProductForm, name: e.target.value })}
                                  className="w-full px-4 py-2.5 border border-[#D5CBBF] rounded-xl bg-white text-xs font-bold focus:outline-none"
                                />
                              </div>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                              <div>
                                <label className="text-[9px] font-extrabold text-slate-500 uppercase tracking-widest block mb-1.5">Precio de Fábrica ($ USD) *</label>
                                <input 
                                  type="number"
                                  step="0.01"
                                  required
                                  placeholder="Ej: 45.00"
                                  value={newProductForm.price}
                                  onChange={(e) => setNewProductForm({ ...newProductForm, price: e.target.value })}
                                  className="w-full px-4 py-2.5 border border-[#D5CBBF] rounded-xl bg-white text-xs font-black text-pink-600 focus:outline-none"
                                />
                              </div>
                              <div>
                                <label className="text-[9px] font-extrabold text-slate-500 uppercase tracking-widest block mb-1.5">Tamaño / Envase</label>
                                <input 
                                  type="text"
                                  placeholder="Ej. 12 Vasos de 200ml"
                                  value={newProductForm.size}
                                  onChange={(e) => setNewProductForm({ ...newProductForm, size: e.target.value })}
                                  className="w-full px-4 py-2.5 border border-[#D5CBBF] rounded-xl bg-white text-xs font-bold focus:outline-none"
                                />
                              </div>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                              <div>
                                <label className="text-[9px] font-extrabold text-slate-500 uppercase tracking-widest block mb-1.5">Etiqueta (Badge)</label>
                                <input 
                                  type="text"
                                  placeholder="Ej. Campaña, Especial, Lanzamiento"
                                  value={newProductForm.badge}
                                  onChange={(e) => setNewProductForm({ ...newProductForm, badge: e.target.value })}
                                  className="w-full px-4 py-2.5 border border-[#D5CBBF] rounded-xl bg-white text-xs font-extrabold focus:outline-none"
                                />
                              </div>
                              <div>
                                <label className="text-[9px] font-extrabold text-slate-500 uppercase tracking-widest block mb-1.5">Pedido Mínimo (Uds)</label>
                                <input 
                                  type="number"
                                  value={newProductForm.min_qty}
                                  onChange={(e) => setNewProductForm({ ...newProductForm, min_qty: e.target.value })}
                                  className="w-full px-4 py-2.5 border border-[#D5CBBF] rounded-xl bg-white text-xs font-bold focus:outline-none"
                                />
                              </div>
                            </div>

                            {/* Image Uploader widget for new product */}
                            <div className="p-4 bg-[#FAF6EE] rounded-2xl border border-[#F5EFE6] space-y-3">
                              <label className="text-[9px] font-extrabold text-slate-500 uppercase tracking-widest block">Subir Imagen del Producto</label>
                              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
                                <input 
                                  type="file"
                                  accept="image/*"
                                  onChange={(e) => {
                                    const file = e.target.files[0];
                                    if (file) {
                                      const formData = new FormData();
                                      formData.append("file", file);
                                      setLoading(true);
                                      axios.post(`${API}/upload`, formData, {
                                        headers: { "Content-Type": "multipart/form-data" }
                                      }).then(res => {
                                        const uploadedUrl = `${BACKEND_URL}${res.data.url}`;
                                        setNewProductForm({ ...newProductForm, image: uploadedUrl });
                                        toast.success("¡Imagen subida con éxito!");
                                      }).catch(err => {
                                        console.error(err);
                                        toast.error("Error al subir la imagen.");
                                      }).finally(() => setLoading(false));
                                    }
                                  }}
                                  className="block w-full text-xs text-slate-500 file:mr-4 file:py-2.5 file:px-4 file:rounded-xl file:border-0 file:text-xs file:font-extrabold file:bg-pink-50 file:text-pink-700 hover:file:bg-pink-100 cursor-pointer"
                                />
                              </div>
                              {newProductForm.image && (
                                <div className="mt-2 text-[10px] text-slate-400 font-mono truncate">
                                  <span className="font-extrabold text-slate-500 mr-2 uppercase text-[9px]">Imagen:</span>
                                  {newProductForm.image}
                                </div>
                              )}
                            </div>

                            <div>
                              <label className="text-[9px] font-extrabold text-slate-500 uppercase tracking-widest block mb-1.5">Descripción para Clientes</label>
                              <textarea 
                                value={newProductForm.description}
                                rows={3}
                                onChange={(e) => setNewProductForm({ ...newProductForm, description: e.target.value })}
                                className="w-full px-4 py-2.5 border border-[#D5CBBF] rounded-xl bg-white text-xs text-slate-600 leading-relaxed font-manrope focus:outline-none"
                              />
                            </div>

                            <button
                              type="submit"
                              disabled={loading}
                              className="w-full py-4 rounded-2xl bg-green-600 hover:bg-green-700 text-white font-manrope font-extrabold text-xs uppercase tracking-widest transition-all flex items-center justify-center gap-2 shadow-sm"
                            >
                              <Check className="h-5 w-5" />
                              <span>Crear Campaña / Combo de Productos</span>
                            </button>
                          </form>
                        )}

                        <div className="space-y-8">
                          {products.map((product) => {
                            const editForm = editingProductsList[product.id] || product;

                            return (
                              <div key={product.id} className="border border-[#F5EFE6] rounded-3xl p-6 sm:p-8 bg-[#FAF6EE]/30 text-xs text-left space-y-5 relative overflow-hidden shadow-sm hover:shadow-md transition-all">
                                <div className="absolute top-0 left-0 w-2.5 h-full bg-[#00BACF]" />
                                
                                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-slate-100 pb-4">
                                  <div className="flex items-center gap-4">
                                    <img src={editForm.image} alt={editForm.name} className="w-16 h-16 rounded-2xl object-cover border border-[#F5EFE6] bg-white shadow-sm" />
                                    <div>
                                      <span className="text-[9px] text-[#A69C95] font-black uppercase tracking-wider bg-slate-100 px-2 py-0.5 rounded-md">ID: {product.id}</span>
                                      <h5 className="font-jakarta font-black text-slate-800 text-base mt-1.5">{product.name}</h5>
                                    </div>
                                  </div>
                                  
                                  <div className="flex items-center gap-2">
                                    <span className="text-[10px] text-[#00BACF] bg-white px-3 py-1 rounded-full font-black tracking-widest border uppercase w-fit">
                                      Activo
                                    </span>
                                    
                                    <button
                                      onClick={() => handleDeleteProductCMS(product.id, product.name)}
                                      className="p-2 bg-red-50 hover:bg-red-100 text-red-500 rounded-xl transition-all border border-red-100"
                                      title="Eliminar del catálogo"
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </button>
                                  </div>
                                </div>

                              <div className="grid grid-cols-2 gap-4">
                                <div>
                                  <label className="text-[9px] font-extrabold text-slate-500 uppercase tracking-widest block mb-1">Nombre Comercial</label>
                                  <input 
                                    type="text"
                                    value={editForm.name || ""}
                                    onChange={(e) => handleUpdateProductField(product.id, "name", e.target.value)}
                                    className="w-full px-3 py-2 border border-[#D5CBBF] rounded-xl bg-white text-xs focus:ring-1 focus:ring-[#00BACF] focus:outline-none font-bold"
                                  />
                                </div>
                                <div>
                                  <label className="text-[9px] font-extrabold text-slate-500 uppercase tracking-widest block mb-1">Precio Unitario ($ USD)</label>
                                  <input 
                                    type="number"
                                    step="0.01"
                                    value={editForm.price || 0}
                                    onChange={(e) => handleUpdateProductField(product.id, "price", e.target.value)}
                                    className="w-full px-3 py-2 border border-[#D5CBBF] rounded-xl bg-white text-xs focus:ring-1 focus:ring-[#00BACF] focus:outline-none font-black text-pink-600"
                                  />
                                </div>
                              </div>

                              <div className="grid grid-cols-3 gap-3">
                                <div>
                                  <label className="text-[9px] font-extrabold text-slate-500 uppercase tracking-widest block mb-1">Tamaño / Envase</label>
                                  <input 
                                    type="text"
                                    value={editForm.size || ""}
                                    onChange={(e) => handleUpdateProductField(product.id, "size", e.target.value)}
                                    className="w-full px-3 py-2 border border-[#D5CBBF] rounded-xl bg-white text-xs focus:ring-1 focus:ring-[#00BACF] focus:outline-none font-bold"
                                  />
                                </div>
                                <div>
                                  <label className="text-[9px] font-extrabold text-slate-500 uppercase tracking-widest block mb-1">Etiqueta Badge</label>
                                  <input 
                                    type="text"
                                    value={editForm.badge || ""}
                                    onChange={(e) => handleUpdateProductField(product.id, "badge", e.target.value)}
                                    className="w-full px-3 py-2 border border-[#D5CBBF] rounded-xl bg-white text-xs focus:ring-1 focus:ring-[#00BACF] focus:outline-none font-extrabold"
                                  />
                                </div>
                                <div>
                                  <label className="text-[9px] font-extrabold text-slate-500 uppercase tracking-widest block mb-1">Mínimo Compra</label>
                                  <input 
                                    type="number"
                                    value={editForm.min_qty || 1}
                                    onChange={(e) => handleUpdateProductField(product.id, "min_qty", e.target.value)}
                                    className="w-full px-3 py-2 border border-[#D5CBBF] rounded-xl bg-white text-xs focus:ring-1 focus:ring-[#00BACF] focus:outline-none font-bold"
                                  />
                                </div>
                              </div>

                              <div>
                                <label className="text-[9px] font-extrabold text-slate-500 uppercase tracking-widest block mb-1">URL de Imagen</label>
                                <input 
                                  type="text"
                                  value={editForm.image || ""}
                                  onChange={(e) => handleUpdateProductField(product.id, "image", e.target.value)}
                                  className="w-full px-3 py-2 border border-[#D5CBBF] rounded-xl bg-white text-xs focus:ring-1 focus:ring-[#00BACF] focus:outline-none text-slate-500 font-mono"
                                />
                              </div>

                              <div>
                                <label className="text-[9px] font-extrabold text-slate-500 uppercase tracking-widest block mb-1">Descripción de Producto</label>
                                <textarea 
                                  value={editForm.description || ""}
                                  rows={2}
                                  onChange={(e) => handleUpdateProductField(product.id, "description", e.target.value)}
                                  className="w-full px-3 py-2 border border-[#D5CBBF] rounded-xl bg-white text-xs focus:ring-1 focus:ring-[#00BACF] focus:outline-none text-slate-600 leading-relaxed font-manrope"
                                />
                              </div>

                              <button
                                onClick={() => handleSaveProductCMS(product.id)}
                                disabled={loading}
                                data-testid={`cms-save-${product.id}`}
                                className="w-full py-3.5 rounded-2xl bg-green-600 hover:bg-green-700 text-white font-manrope font-extrabold text-xs uppercase tracking-wider transition-colors flex items-center justify-center gap-2"
                              >
                                <Check className="h-4.5 w-4.5" />
                                <span>Guardar Cambios para {product.id}</span>
                              </button>
                            </div>
                          );
                        })}
                      </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div>
                    <div className="flex items-center gap-2 mb-4">
                      <ClipboardList className="h-5 w-5 text-[#00BACF]" />
                      <h4 className="font-jakarta font-extrabold text-lg text-[#2E2825]">Mis Pedidos Registrados (Firebase)</h4>
                    </div>

                    {userOrders.length === 0 ? (
                      <div className="py-12 text-center bg-slate-50 rounded-2xl border border-dashed border-slate-200">
                        <ClipboardList className="h-10 w-10 text-slate-300 mx-auto stroke-[1.2]" />
                        <p className="text-xs text-slate-400 mt-2">Aún no tienes pedidos registrados bajo esta cuenta.</p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {userOrders.map((order, idx) => (
                          <div key={idx} className="border border-slate-100 p-5 rounded-2xl bg-white shadow-sm hover:shadow-md transition-all text-left">
                            <div className="flex justify-between items-start border-b border-slate-100 pb-3 mb-3 text-xs">
                              <div>
                                <span className="text-[#A69C95] font-bold block uppercase tracking-wider">PEDIDO ID:</span>
                                <span className="font-manrope font-bold text-slate-800">{order.stripe_session_id.substring(0, 15)}...</span>
                              </div>
                              <div className="text-right">
                                <span className="text-[#A69C95] font-bold block uppercase tracking-wider">FECHA:</span>
                                <span className="font-manrope text-slate-500">{new Date(order.created_at).toLocaleDateString()}</span>
                              </div>
                            </div>

                            {/* Items Purchased list */}
                            <div className="space-y-1.5 mb-3 text-xs">
                              {order.items.map((it, i) => (
                                <div key={i} className="flex justify-between text-slate-600">
                                  <span>{it.quantity}x {it.name}</span>
                                  <span className="font-bold">{formatPrice(it.price * it.quantity)}</span>
                                </div>
                              ))}
                            </div>

                            <div className="flex justify-between items-center pt-3 border-t border-slate-100 text-xs">
                              <div className="flex items-center gap-2">
                                <span className={`px-2.5 py-1 rounded-full font-manrope font-extrabold text-[9px] uppercase tracking-wider ${
                                  order.payment_status === "paid" ? "bg-green-100 text-green-700" : "bg-yellow-100 text-yellow-700"
                                }`}>
                                  {order.payment_status === "paid" ? "Pagado" : "Pendiente"}
                                </span>
                              </div>
                              <span className="font-jakarta font-black text-sm text-pink-600">
                                Total: {formatPrice(order.total_amount)}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Profile Footer */}
              <div className="p-6 border-t border-slate-100 bg-[#FAF6EE]/50 flex justify-end gap-3">
                <button 
                  onClick={handleLogout}
                  data-testid="auth-logout-button-bottom"
                  className="px-6 py-3 rounded-full border border-red-200 text-red-500 hover:bg-red-50 font-bold text-xs uppercase tracking-wider transition-colors duration-300"
                >
                  Cerrar Sesión
                </button>
                <button 
                  onClick={() => setIsProfileOpen(false)}
                  className="px-6 py-3 rounded-full bg-slate-900 hover:bg-slate-800 text-white font-bold text-xs uppercase tracking-wider transition-colors duration-300"
                >
                  Cerrar Panel
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* --- Authentication Modal (Login / Register) --- */}
      <AnimatePresence>
        {authModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center px-4 overflow-y-auto py-12">
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.6 }}
              exit={{ opacity: 0 }}
              onClick={() => setAuthModal(null)}
              className="fixed inset-0 bg-slate-950"
            />

            <motion.div 
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-[2rem] w-full max-w-md p-8 shadow-2xl relative z-10 border border-[#FAF6EE] text-left"
            >
              <button 
                onClick={() => setAuthModal(null)}
                className="absolute top-6 right-6 p-2 rounded-full hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-all"
              >
                <X className="h-5 w-5" />
              </button>

              {/* Login View */}
              {authModal === "login" ? (
                <form onSubmit={handleLogin} className="space-y-5">
                  <div className="text-center pb-4 border-b border-slate-100">
                    <h3 className="font-jakarta font-extrabold text-2xl text-slate-950">Iniciar Sesión</h3>
                    <p className="text-xs text-slate-400 mt-1">Ingresa a tu cuenta de distribuidor.</p>
                  </div>

                  <div>
                    <label className="text-[10px] font-extrabold text-slate-600 uppercase tracking-widest block mb-1">Correo Electrónico *</label>
                    <div className="relative">
                      <Mail className="absolute left-3.5 top-3 h-4 w-4 text-slate-400" />
                      <input 
                        type="email" 
                        required
                        value={loginForm.email}
                        onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                        data-testid="auth-login-email-input"
                        placeholder="ejemplo@email.com"
                        className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-[#D5CBBF] text-sm focus:outline-none focus:ring-2 focus:ring-[#00BACF] bg-white"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="text-[10px] font-extrabold text-slate-600 uppercase tracking-widest block mb-1">Contraseña *</label>
                    <div className="relative">
                      <Lock className="absolute left-3.5 top-3 h-4 w-4 text-slate-400" />
                      <input 
                        type="password" 
                        required
                        value={loginForm.password}
                        onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                        data-testid="auth-login-password-input"
                        placeholder="••••••••"
                        className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-[#D5CBBF] text-sm focus:outline-none focus:ring-2 focus:ring-[#00BACF] bg-white"
                      />
                    </div>
                  </div>

                  <button 
                    type="submit"
                    disabled={loading}
                    data-testid="auth-login-submit"
                    className="w-full py-4 rounded-full bg-pink-500 hover:bg-pink-600 text-white font-manrope font-bold text-xs tracking-widest uppercase transition-all duration-300 flex items-center justify-center"
                  >
                    {loading ? (
                      <span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                    ) : (
                      "Entrar a Mi Cuenta"
                    )}
                  </button>

                  <div className="text-center pt-2 text-xs">
                    <span className="text-[#A69C95]">¿No tienes cuenta? </span>
                    <button 
                      type="button"
                      onClick={() => setAuthModal("register")}
                      className="text-[#00BACF] font-bold hover:underline"
                    >
                      Regístrate Aquí
                    </button>
                  </div>
                </form>
              ) : (
                // Register View
                <form onSubmit={handleRegister} className="space-y-4">
                  <div className="text-center pb-3 border-b border-slate-100">
                    <h3 className="font-jakarta font-extrabold text-2xl text-slate-950">Crear Cuenta</h3>
                    <p className="text-xs text-slate-400 mt-1">Regístrate como distribuidor o emprendedor.</p>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-[10px] font-extrabold text-slate-600 uppercase tracking-widest block mb-1">Nombre *</label>
                      <input 
                        type="text" 
                        required
                        value={registerForm.firstName}
                        onChange={(e) => setRegisterForm({ ...registerForm, firstName: e.target.value })}
                        data-testid="auth-first-name-input"
                        placeholder="Juan"
                        className="w-full px-4 py-2.5 rounded-xl border border-[#D5CBBF] text-sm focus:outline-none focus:ring-2 focus:ring-[#00BACF] bg-white"
                      />
                    </div>
                    <div>
                      <label className="text-[10px] font-extrabold text-slate-600 uppercase tracking-widest block mb-1">Apellido *</label>
                      <input 
                        type="text" 
                        required
                        value={registerForm.lastName}
                        onChange={(e) => setRegisterForm({ ...registerForm, lastName: e.target.value })}
                        data-testid="auth-last-name-input"
                        placeholder="Pérez"
                        className="w-full px-4 py-2.5 rounded-xl border border-[#D5CBBF] text-sm focus:outline-none focus:ring-2 focus:ring-[#00BACF] bg-white"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="text-[10px] font-extrabold text-slate-600 uppercase tracking-widest block mb-1">Celular / Teléfono *</label>
                    <div className="relative">
                      <Phone className="absolute left-3.5 top-3 h-4 w-4 text-slate-400" />
                      <input 
                        type="tel" 
                        required
                        value={registerForm.phone}
                        onChange={(e) => setRegisterForm({ ...registerForm, phone: e.target.value })}
                        data-testid="auth-phone-input"
                        placeholder="Ej. +584121234567"
                        className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-[#D5CBBF] text-sm focus:outline-none focus:ring-2 focus:ring-[#00BACF] bg-white"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="text-[10px] font-extrabold text-slate-600 uppercase tracking-widest block mb-1">Correo Electrónico *</label>
                    <div className="relative">
                      <Mail className="absolute left-3.5 top-3 h-4 w-4 text-slate-400" />
                      <input 
                        type="email" 
                        required
                        value={registerForm.email}
                        onChange={(e) => setRegisterForm({ ...registerForm, email: e.target.value })}
                        data-testid="auth-email-input"
                        placeholder="ejemplo@email.com"
                        className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-[#D5CBBF] text-sm focus:outline-none focus:ring-2 focus:ring-[#00BACF] bg-white"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="text-[10px] font-extrabold text-slate-600 uppercase tracking-widest block mb-1">Contraseña *</label>
                    <div className="relative">
                      <Lock className="absolute left-3.5 top-3 h-4 w-4 text-slate-400" />
                      <input 
                        type="password" 
                        required
                        value={registerForm.password}
                        onChange={(e) => setRegisterForm({ ...registerForm, password: e.target.value })}
                        data-testid="auth-password-input"
                        placeholder="Contraseña segura"
                        className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-[#D5CBBF] text-sm focus:outline-none focus:ring-2 focus:ring-[#00BACF] bg-white"
                      />
                    </div>
                  </div>

                  <button 
                    type="submit"
                    disabled={loading}
                    data-testid="auth-register-submit"
                    className="w-full py-4 rounded-full bg-pink-500 hover:bg-pink-600 text-white font-manrope font-bold text-xs tracking-widest uppercase transition-all duration-300 flex items-center justify-center"
                  >
                    {loading ? (
                      <span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                    ) : (
                      "Crear Mi Cuenta"
                    )}
                  </button>

                  <div className="text-center pt-2 text-xs">
                    <span className="text-[#A69C95]">¿Ya tienes cuenta? </span>
                    <button 
                      type="button"
                      onClick={() => setAuthModal("login")}
                      className="text-[#00BACF] font-bold hover:underline"
                    >
                      Inicia Sesión
                    </button>
                  </div>
                </form>
              )}
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* --- Stripe Success Receipt Modal --- */}
      <AnimatePresence>
        {isSuccessOpen && successOrder && (
          <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto px-6 py-12">
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.6 }}
              exit={{ opacity: 0 }}
              onClick={() => {
                setIsSuccessOpen(false);
                window.history.replaceState({}, document.title, window.location.pathname);
              }}
              className="fixed inset-0 bg-slate-950"
            />

            <motion.div 
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-[2rem] w-full max-w-xl p-8 shadow-2xl relative z-10 border border-pink-100 text-left animate-pulse-soft"
            >
              <div className="text-center pb-6 border-b border-dashed border-slate-200">
                <div className="mx-auto w-16 h-16 rounded-full bg-green-50 flex items-center justify-center text-green-500 mb-4">
                  <Check className="h-8 w-8 stroke-[3]" />
                </div>
                <h3 className="font-jakarta font-extrabold text-2xl text-slate-950">¡Pago Exitoso!</h3>
                <p className="text-xs text-slate-500 mt-1 font-semibold">Tu pedido mayorista ha sido registrado con éxito en la base de datos Firebase.</p>
              </div>

              {/* Receipt info */}
              <div className="py-6 space-y-4 text-sm font-medium font-manrope">
                <div className="flex justify-between">
                  <span className="text-slate-400 font-bold uppercase tracking-wider text-[10px]">ID Pedido:</span>
                  <span className="text-slate-800 font-manrope font-semibold">{successOrder.stripe_session_id.substring(0, 15)}...</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400 font-bold uppercase tracking-wider text-[10px]">Cliente:</span>
                  <span className="text-slate-800 font-bold">{successOrder.client_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400 font-bold uppercase tracking-wider text-[10px]">Teléfono:</span>
                  <span className="text-slate-800">{successOrder.client_phone}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400 font-bold uppercase tracking-wider text-[10px]">Entrega:</span>
                  <span className="text-slate-800 font-bold uppercase tracking-wide text-xs">
                    {successOrder.delivery_type === "delivery" ? "Delivery / Despacho" : "Retiro en Tienda"}
                  </span>
                </div>
                {successOrder.delivery_type === "delivery" && (
                  <div className="flex flex-col gap-1 pt-1">
                    <span className="text-slate-400 font-bold uppercase tracking-wider text-[10px]">Dirección de Despacho:</span>
                    <span className="text-slate-700 bg-slate-50 p-3 rounded-xl border border-slate-100 text-xs leading-relaxed">
                      {successOrder.delivery_address}
                    </span>
                  </div>
                )}

                {/* Items Bought */}
                <div className="border-t border-slate-100 pt-4 mt-4">
                  <h4 className="font-jakarta font-extrabold text-xs text-slate-900 mb-3">Productos Comprados:</h4>
                  <div className="space-y-3">
                    {successOrder.items.map((item, index) => (
                      <div key={index} className="flex justify-between text-xs font-semibold">
                        <span className="text-slate-600">{item.quantity}x {item.name}</span>
                        <span className="text-slate-950 font-bold font-manrope">${(item.price * item.quantity).toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Total */}
                <div className="flex justify-between border-t border-slate-200/50 pt-4 mt-4 text-base">
                  <span className="font-jakarta font-extrabold text-slate-950">Total Pagado:</span>
                  <span className="font-jakarta font-black text-pink-600 text-lg">${successOrder.total_amount.toFixed(2)}</span>
                </div>
              </div>

              {/* WhatsApp confirmation CTA */}
              <div className="pt-6 border-t border-slate-100 space-y-4">
                <button 
                  onClick={() => sendWhatsAppNotification(successOrder)}
                  data-testid="whatsapp-notify-button"
                  className="w-full py-4 rounded-full bg-green-500 hover:bg-green-600 text-white font-manrope font-bold text-xs tracking-widest uppercase transition-all duration-300 shadow-[0_8px_30px_rgba(34,197,94,0.15)] flex items-center justify-center gap-3 hover:-translate-y-0.5"
                >
                  <MessageCircle className="h-5 w-5 fill-current" />
                  <span>Notificar Pedido por WhatsApp</span>
                </button>
                <button 
                  onClick={() => {
                    setIsSuccessOpen(false);
                    window.history.replaceState({}, document.title, window.location.pathname);
                  }}
                  className="w-full py-3 rounded-full hover:bg-slate-100 text-slate-500 font-manrope font-bold text-[10px] tracking-wider uppercase transition-colors duration-300 text-center"
                >
                  Cerrar Recibo
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}
